import React, { useState } from 'react';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { NewSearch } from '../Components/PIM/common/NewSearch';
import { NewInput, InputAdornmentButton } from '../Components/PIM/common/NewInput';
import { NewTextArea } from '../Components/PIM/common/NewTextArea';
import { NewSelectMultiSelect } from '../Components/PIM/common/NewSelectMultiSelect';
import { NewCheckbox } from '../Components/PIM/common/NewCheckbox';
import { NewRadio } from '../Components/PIM/common/NewRadio';
import { NewDatePicker } from '../Components/PIM/common/NewDatePicker';
import { BombiixCard } from '../Components/Card/BombiixCard';
import { BombiixAGGrid } from '../Components/BombiixAGGrid';
import { BombiixModal } from '../Components/BombiixModal';
import { PimConfirmPopup } from '../Components/PIM/common';
import { BombiixSidePanel } from '../Components/BombiixSidePanel';
import { BombiixInfoPopover } from '../Components/BombiixInfoPopover';
import { BombiixChip } from '../Components/BombiixChip';
import { BombiixChipSelect } from '../Components/BombiixChipSelect';
import { BombiixSortButton } from '../Components/BombiixSortButton';
import { BombiixRearrangePanel } from '../Components/BombiixRearrangePanel';
import { BombiixApprovalBar } from '../Components/BombiixApprovalBar';
import { Floaty, FloatyButton, FloatyGroup } from '../Components/Floaty';
import CommonTabSwitch from '../Components/CommonTabSwitch/CommonTabSwitch';
import CommonRichTextField from '../Components/CommonRichTextField/CommonRichTextField';
import { richTextLength } from '../utils/html-text';
import {
  EditIcon,
  DownloadIcon,
  ProductGroupIcon,
  SummaryIcon,
  CloseIcon,
  AddIcon,
  DeleteIcon,
  InfoIcon,
  ArchiveIcon,
  DuplicateIcon,
  FilterIcon,
  SortIcon,
  SearchIcon,
  PublishIcon,
  ActiveIcon,
  ProgressIndicatorIcon,
  EmoteIcon,
  ImageIcon,
  PencilIcon,
  CalendarIcon2,
  FaceIcon,
  GlobeIcon,
  PresentationIcon,
  CameraIcon,
  WeatherIcon,
  TestReportIcon,
  FileSearchIcon,
  ShareIcon,
  TableViewIcon,
  GroupIcon,
  SubmitAndDownloadIcon,
  ChevronIcon,
} from '../bombiix-icon';
import { BombiixDropzone } from '../Components/BombiixDropzone';

const FIBRE_OPTIONS = [
  { label: 'Cotton', value: 'cotton' },
  { label: 'Polyester', value: 'polyester' },
  { label: 'Wool', value: 'wool' },
  { label: 'Linen', value: 'linen' },
  { label: 'Silk', value: 'silk' },
];

const SORT_OPTIONS = [
  { label: 'Name (Ascending)', propertyName: 'name', sortOrder: 'Asc' },
  { label: 'Name (Descending)', propertyName: 'name', sortOrder: 'Desc' },
];

// Three-option set from the Figma spec. "Approved with comments" is not yet
// understood by any approval endpoint — the bar takes options as a prop so
// each module passes only the statuses its own API accepts. `variant: 'red'`
// gives Rejected its destructive fill when selected (Figma node 7968:139640).
const APPROVAL_OPTIONS_THREE = [
  { label: 'Approved', value: 1 },
  { label: 'Approved with comments', value: 2 },
  { label: 'Rejected', value: 0, variant: 'red' },
];

// The two-option set matching every approval endpoint that exists today.
const APPROVAL_OPTIONS_TWO = [
  { label: 'Approved', value: 1 },
  { label: 'Rejected', value: 0, variant: 'red' },
];

const STATUS_TYPE_OPTIONS = [
  { label: 'Lab Dip', value: 'lab-dip' },
  { label: 'Strike Off', value: 'strike-off' },
  { label: 'Fit Sample', value: 'fit-sample' },
];

const SUPPLIER_OPTIONS = [
  { label: 'Arvind Mills', value: 'arvind' },
  { label: 'Shahi Exports', value: 'shahi' },
  { label: 'Epic Group', value: 'epic' },
];

/**
 * Segmented pill tab-switch demo set (Figma node 12358:160579). `eventName` is
 * passed through i18n `t()` by the component, so plain labels render as-is.
 */
const TAB_SWITCH_OPTIONS = [
  { eventKey: 'materials', eventName: 'Materials' },
  { eventKey: 'artwork', eventName: 'Artwork & Applications' },
  { eventKey: 'trims', eventName: 'Trims' },
  { eventKey: 'labels', eventName: 'Labels' },
  { eventKey: 'packaging', eventName: 'Packaging' },
];

/**
 * Long set (Figma node 12948:715059) — exceeds a constrained container so the
 * trailing tabs collapse into the MoreIcon overflow popover.
 */
const TAB_SWITCH_OVERFLOW_OPTIONS = [
  { eventKey: 'costing', eventName: 'Costing Template' },
  { eventKey: 'costing2', eventName: 'Costing Template 2' },
  { eventKey: 'budget', eventName: 'Budget Overview' },
  { eventKey: 'expense', eventName: 'Expense Breakdown' },
  { eventKey: 'revenue', eventName: 'Revenue Projection' },
  { eventKey: 'profit', eventName: 'Profit Analysis' },
  { eventKey: 'resource', eventName: 'Resource Allocation' },
  { eventKey: 'efficiency', eventName: 'Cost Efficiency' },
  { eventKey: 'summary', eventName: 'Financial Summary' },
  { eventKey: 'investment', eventName: 'Investment Plan' },
  { eventKey: 'operational', eventName: 'Operational Costs' },
];

/**
 * Bombiix master design-system showcase. Renders every reusable design-token
 * class from `assets/scss/master-design.scss` so developers can eyeball each
 * variant + state at /master-design without navigating to a real feature.
 * Keep additions in sync with the SCSS partials.
 */
function MasterDesign() {
  const [rearrangeOpen, setRearrangeOpen] = useState(false);
  const [reorderDemo, setReorderDemo] = useState([
    { id: 'tooling', label: 'BOM Type:', text: 'Tooling' },
    { id: 'materials', label: 'BOM Type:', text: 'Materials' },
    { id: 'trims', label: 'BOM Type:', text: 'Trims' },
    { id: 'labels', label: 'BOM Type:', text: 'Labels' },
    { id: 'artwork', label: 'BOM Type:', text: 'Artwork & Applications' },
    { id: 'packaging', label: 'BOM Type:', text: 'Packaging' },
  ]);
  const [searchValue, setSearchValue] = useState('');
  const [searchFilled, setSearchFilled] = useState('Cotton');
  const [searchOnEnterValue, setSearchOnEnterValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [inputFilled, setInputFilled] = useState('Some text');
  const [singleSelect, setSingleSelect] = useState(null);
  const [multiSelect, setMultiSelect] = useState([]);
  const [chipSelectValue, setChipSelectValue] = useState([FIBRE_OPTIONS[0], FIBRE_OPTIONS[2]]);
  const [sortValue, setSortValue] = useState(null);
  const [selectClearable, setSelectClearable] = useState(FIBRE_OPTIONS[0]);
  const [selectLabelIdle, setSelectLabelIdle] = useState(null);
  const [selectLabelValue, setSelectLabelValue] = useState(FIBRE_OPTIONS[0]);
  const [selectLabelMulti, setSelectLabelMulti] = useState([FIBRE_OPTIONS[1], FIBRE_OPTIONS[2]]);
  const [selectLabelInvalid, setSelectLabelInvalid] = useState(FIBRE_OPTIONS[1]);
  const [checkOne, setCheckOne] = useState(false);
  const [checkTwo, setCheckTwo] = useState(true);
  const [radioValue, setRadioValue] = useState('cotton');
  const [dateIdle, setDateIdle] = useState(null);
  const [dateFilled, setDateFilled] = useState(new Date());
  const [dateInvalid, setDateInvalid] = useState(new Date());
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [modalOpenBasic, setModalOpenBasic] = useState(false);
  const [modalOpenReadOnly, setModalOpenReadOnly] = useState(false);
  const [modalOpenBlocking, setModalOpenBlocking] = useState(false);
  const [modalOpenFooter, setModalOpenFooter] = useState(false);
  const [modalOpenDelete, setModalOpenDelete] = useState(false);
  const [confirmOpenBar, setConfirmOpenBar] = useState(false);
  const [modalInputValue, setModalInputValue] = useState('');
  const [descValue, setDescValue] = useState('Dummy Data');
  const [descSelected, setDescSelected] = useState(false);
  const [switchOnValue, setSwitchOnValue] = useState('');
  const [switchOffValue, setSwitchOffValue] = useState('');
  const [switchOn, setSwitchOn] = useState(true);
  const [switchOff, setSwitchOff] = useState(false);
  const [sidePanelBasicOpen, setSidePanelBasicOpen] = useState(false);
  const [sidePanelSaveOpen, setSidePanelSaveOpen] = useState(false);
  const [sidePanelHeaderRightOpen, setSidePanelHeaderRightOpen] = useState(false);
  const [sidePanelCommentValue, setSidePanelCommentValue] = useState('');
  const [approvalFull, setApprovalFull] = useState(1);
  const [approvalFullType, setApprovalFullType] = useState(null);
  const [approvalFullDate, setApprovalFullDate] = useState(null);
  const [approvalFullSupplier, setApprovalFullSupplier] = useState(null);
  const [approvalFullComment, setApprovalFullComment] = useState('');
  const [approvalTwo, setApprovalTwo] = useState(null);
  const [approvalTwoDate, setApprovalTwoDate] = useState(null);
  const [approvalTwoComment, setApprovalTwoComment] = useState('');
  const [approvalValidate, setApprovalValidate] = useState(0);
  const [approvalValidateComment, setApprovalValidateComment] = useState('');
  const [approvalMinimal, setApprovalMinimal] = useState(0);
  const [activeTabSwitch, setActiveTabSwitch] = useState('materials');
  const [activeTabSwitchDisabled, setActiveTabSwitchDisabled] = useState('artwork');
  const [activeTabSwitchOverflow, setActiveTabSwitchOverflow] = useState('costing');
  const [richTextEmpty, setRichTextEmpty] = useState('');
  const [richTextFilled, setRichTextFilled] = useState(
    '<p>Cotton lining, <strong>breathable</strong>. <span style="color:hsl(120,75%,30%);">Approved</span> by the mill.</p>',
  );
  const [richTextCapped, setRichTextCapped] = useState('<p>Short note.</p>');

  /**
   * Comment-required demo: Rejected (value 0) needs a comment, so the field is
   * marked invalid while it is empty. The bar owns no validation — the caller
   * derives `commentInvalid` from its own rule and passes it in.
   */
  const approvalValidateCommentInvalid = approvalValidate === 0 && approvalValidateComment.trim().length === 0;

  /**
   * Product Description cell — NewInput with checkbox startAdornment and a
   * `nn/200` character-counter endAdornment. Reads the row's `description`
   * and `selected` fields via ICellRendererParams.
   */
  function DescriptionAdornmentCell(params) {
    const row = params?.data;
    if (!row) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%', padding: '4px 0' }}>
        <NewInput
          placeholder="Product Description"
          value={row.description}
          maxLength={200}
          onChange={(event) => params.onRowChange?.(row.id, 'description', event.target.value)}
          startAdornment={<NewCheckbox size="sm" checked={row.selected} onChange={(event) => params.onRowChange?.(row.id, 'selected', event.target.checked)} />}
          endAdornment={<span className="bombiix-input__counter">{(row.description ?? '').length}/200</span>}
        />
      </div>
    );
  }

  /**
   * Drp cell factory — returns a NewInput cell renderer with a `+` end
   * adornment. `field` picks which row property the input reads/writes so
   * one factory covers Drp1 and Drp2.
   */
  const makeDrpAdornmentCell = (field, ariaLabel) => {
    function DrpAdornmentCell(params) {
      const row = params?.data;
      if (!row) return null;
      return (
        <div style={{ display: 'flex', alignItems: 'center', height: '100%', padding: '4px 0' }}>
          <NewInput
            placeholder={ariaLabel}
            value={row[field] ?? ''}
            onChange={(event) => params.onRowChange?.(row.id, field, event.target.value)}
            endAdornment={<InputAdornmentButton icon={AddIcon} ariaLabel={`Add ${ariaLabel} option`} onClick={() => {}} />}
          />
        </div>
      );
    }
    DrpAdornmentCell.displayName = `DrpAdornmentCell(${field})`;
    return DrpAdornmentCell;
  };

  /**
   * Licensor cell — NewInput whose startAdornment is the shared
   * `.design-switch` toggle. Text input drives `licensorText`; switch drives
   * `licensor`.
   */
  function LicensorInputCell(params) {
    const row = params?.data;
    if (!row) return null;
    const switchId = `md-grid-licensor-${row.id}`;
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%', padding: '4px 0' }}>
        <NewInput
          placeholder="Licensor"
          value={row.licensorText ?? ''}
          onChange={(event) => params.onRowChange?.(row.id, 'licensorText', event.target.value)}
          startAdornment={
            <div className="design-switch d-inline-flex align-items-center">
              <input
                type="checkbox"
                className="form-check-input mt-0 comment-switch-input"
                id={switchId}
                name={switchId}
                checked={Boolean(row.licensor)}
                onChange={(event) => params.onRowChange?.(row.id, 'licensor', event.target.checked)}
                aria-label="Licensed"
              />
            </div>
          }
        />
      </div>
    );
  }

  /**
   * Price cell — NewInput with a static `£` startAdornment. Bound to the
   * tree-grid's `unitCost` field so the same column powers "unit price"
   * behaviour instead of introducing a separate `price` field.
   */
  function PriceAdornmentCell(params) {
    const row = params?.data;
    if (!row) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%', padding: '4px 0' }}>
        <NewInput
          placeholder="0.00"
          value={row.unitCost ?? ''}
          onChange={(event) => params.onRowChange?.(row.id, 'unitCost', event.target.value)}
          startAdornment="£"
        />
      </div>
    );
  }

  /**
   * Discount cell — NewInput with a static `%` endAdornment (mirrors the
   * tree-grid Discount column but bound to the adornmentTable rows).
   */
  function DiscountAdornmentCell(params) {
    const row = params?.data;
    if (!row) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', height: '100%', padding: '4px 0' }}>
        <NewInput
          placeholder="0"
          value={row.discount ?? ''}
          onChange={(event) => params.onRowChange?.(row.id, 'discount', event.target.value)}
          endAdornment="%"
        />
      </div>
    );
  }

  /**
   * Action cell — bare destructive delete button. Wired to a no-op here for
   * the demo; consumers pass a real handler that removes the row.
   */
  function DeleteActionCell(params) {
    const row = params?.data;
    if (!row) return null;
    const createdBy = row.createdBy ?? 'Chintan Pawar';
    const createdOn = row.createdOn ?? '27-February-2026 06:13 PM';
    const modifiedBy = row.modifiedBy ?? 'Chintan Pawar';
    const modifiedOn = row.modifiedOn ?? '20-May-2026 11:05 AM';
    const infoPopover = (
      <Popover id={`bombiix-info-${row.id}`} className="bombiix-info-popover">
        <Popover.Content>
          <dl>
            <dt>Created:</dt>
            <dd>
              {createdBy} {createdOn}
            </dd>
            <dt>Modified:</dt>
            <dd>
              {modifiedBy} {modifiedOn}
            </dd>
          </dl>
        </Popover.Content>
      </Popover>
    );
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, height: '100%' }}>
        <OverlayTrigger placement="left" trigger={['hover', 'focus']} overlay={infoPopover} rootClose>
          <span className="bombiix-icon-container" tabIndex={0} role="button" aria-label="Row info">
            <InfoIcon />
          </span>
        </OverlayTrigger>
        <InputAdornmentButton icon={DeleteIcon} ariaLabel="Delete row" variant="danger" onClick={() => {}} />
      </div>
    );
  }

  /**
   * Row-selection checkbox cell — 16px `<NewCheckbox size="sm" />` bound to
   * `row.selected`. Same `params.onRowChange` handler pattern as every other
   * editable cell in the grid.
   */
  function RowCheckboxCell(params) {
    const row = params?.data;
    if (!row) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
        <NewCheckbox
          size="sm"
          checked={Boolean(row.selected)}
          onChange={(event) => params.onRowChange?.(row.id, 'selected', event.target.checked)}
          aria-label={`Select row ${row.reference ?? row.id}`}
        />
      </div>
    );
  }

  /**
   * Image cell — Figma spec (node 5272:52253). 32×32 pale card with a subtle
   * drop shadow and a 20×20 `ImageIcon`. Placeholder in the demo; a real
   * consumer would swap the icon for a rendered thumbnail once the row has
   * an image asset.
   */
  function ImageInputCell() {
  return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
      <div
        style={{
          width: 32,
          height: 32,
          borderRadius: 4,
          background: '#fafafa',
          boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ImageIcon size={20} />
      </div>
    </div>
}

  const [gridRows, setGridRows] = useState([
    {
      id: 'r1',
      reference: 'SMVR-001',
      unitCost: '12.50',
      fibre: FIBRE_OPTIONS[0],
      tags: [FIBRE_OPTIONS[0], FIBRE_OPTIONS[3]],
      startDate: '2026-05-01',
      exclude: false,
    },
    { id: 'r2', reference: 'SMVR-002', unitCost: '18.00', fibre: FIBRE_OPTIONS[1], tags: [FIBRE_OPTIONS[1]], startDate: '2026-06-15', exclude: true },
    { id: 'r3', reference: 'SMVR-003', unitCost: '9.75', fibre: FIBRE_OPTIONS[2], tags: [], startDate: null, exclude: false },
  ]);
  const [gridRadio, setGridRadio] = useState('r1');
  const handleGridRowChange = (rowId, field, value) => {
    if (field === 'defaultRow') {
      setGridRadio(rowId);
      return;
    }
    setGridRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)));
  };
  const gridColumnDefs = [
    { headerName: 'Reference', field: 'reference', cellType: 'text', flex: 1 },
    { headerName: 'Unit Cost', field: 'unitCost', cellType: 'currency', width: 140 },
    { headerName: 'Fibre', field: 'fibre', cellType: 'select', cellProps: { options: FIBRE_OPTIONS }, flex: 1 },
    { headerName: 'Tags', field: 'tags', cellType: 'select', cellProps: { options: FIBRE_OPTIONS, isMulti: true, isClearable: true }, flex: 1, minWidth: 220 },
    { headerName: 'Planned Production Start Date (Supplier Confirmed)', field: 'startDate', cellType: 'date', width: 170, required: true },
    { headerName: 'Exclude', field: 'exclude', cellType: 'checkbox', width: 110 },
    { headerName: 'Default', field: 'defaultRow', cellType: 'radio', width: 110 },
    { headerName: 'Created', field: 'created', cellType: 'readonlyText', cellProps: { fallback: '2026-05-01' }, width: 140 },
  ];
  const gridFooterRows = [{ id: '__footer', __cagFooter: true, unitCost: '40.25' }];
  function GridDetailRenderer({ data }) {
  return <div style={{ padding: 12, background: '#eaf3ff' }}>
      <div className="small text-bold mb-1">Row detail — {data?.reference}</div>
      <div className="small text-muted">
        Fibre: {data?.fibre?.label ?? '—'} · Unit cost: £{data?.unitCost ?? '—'} · Excluded: {String(Boolean(data?.exclude))}
      </div>
    </div>
}

  const [productRows, setProductRows] = useState([
    {
      id: 'p1',
      path: ['SMVR-001'],
      reference: 'SMVR-001',
      description: 'Dummy Data',
      selected: true,
      drp1: 'D01',
      drp2: 'D22',
      licensorText: 'Adidas',
      unitCost: '12.50',
      fibre: FIBRE_OPTIONS[0],
      exclude: false,
      licensor: true,
      discount: '5',
    },
    {
      id: 'p1-red',
      path: ['SMVR-001', 'Red'],
      reference: 'Red',
      description: 'Red variant',
      selected: false,
      drp1: 'D01',
      drp2: 'D22',
      licensorText: 'Adidas',
      unitCost: '12.50',
      fibre: FIBRE_OPTIONS[0],
      exclude: false,
      licensor: true,
      discount: '5',
    },
    {
      id: 'p1-blue',
      path: ['SMVR-001', 'Blue'],
      reference: 'Blue',
      description: 'Blue variant',
      selected: false,
      drp1: 'D01',
      drp2: 'D22',
      licensorText: '',
      unitCost: '13.00',
      fibre: FIBRE_OPTIONS[0],
      exclude: false,
      licensor: false,
      discount: '10',
    },
    {
      id: 'p1-black',
      path: ['SMVR-001', 'Black'],
      reference: 'Black',
      description: 'Black variant',
      selected: false,
      drp1: 'D01',
      drp2: 'D22',
      licensorText: 'Adidas',
      unitCost: '13.25',
      fibre: FIBRE_OPTIONS[0],
      exclude: true,
      licensor: true,
      discount: '',
    },
    {
      id: 'p2',
      path: ['SMVR-002'],
      reference: 'SMVR-002',
      description: 'Sample product',
      selected: false,
      drp1: 'D02',
      drp2: 'D21',
      licensorText: '',
      unitCost: '18.00',
      fibre: FIBRE_OPTIONS[1],
      exclude: true,
      licensor: false,
      discount: '15',
    },
    {
      id: 'p2-green',
      path: ['SMVR-002', 'Green'],
      reference: 'Green',
      description: 'Green variant',
      selected: false,
      drp1: 'D02',
      drp2: 'D21',
      licensorText: '',
      unitCost: '18.00',
      fibre: FIBRE_OPTIONS[1],
      exclude: false,
      licensor: false,
      discount: '15',
    },
    {
      id: 'p2-yellow',
      path: ['SMVR-002', 'Yellow'],
      reference: 'Yellow',
      description: 'Yellow variant',
      selected: false,
      drp1: 'D02',
      drp2: 'D21',
      licensorText: '',
      unitCost: '18.75',
      fibre: FIBRE_OPTIONS[1],
      exclude: false,
      licensor: true,
      discount: '20',
    },
    {
      id: 'p3',
      path: ['SMVR-003'],
      reference: 'SMVR-003',
      description: '',
      selected: false,
      drp1: '',
      drp2: '',
      licensorText: 'Puma',
      unitCost: '9.75',
      fibre: FIBRE_OPTIONS[2],
      exclude: false,
      licensor: true,
      discount: '',
    },
    {
      id: 'p3-natural',
      path: ['SMVR-003', 'Natural'],
      reference: 'Natural',
      description: 'Natural variant',
      selected: false,
      drp1: '',
      drp2: '',
      licensorText: 'Puma',
      unitCost: '9.75',
      fibre: FIBRE_OPTIONS[2],
      exclude: false,
      licensor: true,
      discount: '',
    },
  ]);
  const handleProductRowChange = (rowId, field, value) => {
    setProductRows((prev) => prev.map((row) => (row.id === rowId ? { ...row, [field]: value } : row)));
  };

  const productColumnDefs = [
    {
      headerName: '',
      field: 'selected',
      cellRenderer: RowCheckboxCell,
      width: 40,
      minWidth: 40,
      maxWidth: 40,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
    },
    {
      headerName: 'Image',
      field: 'image',
      cellRenderer: ImageInputCell,
      width: 60,
      minWidth: 60,
      maxWidth: 60,
      sortable: false,
      filter: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
    },
    { headerName: 'Reference', field: 'reference', cellType: 'text', width: 160 },
    { headerName: 'Product Description', field: 'description', cellRenderer: DescriptionAdornmentCell, minWidth: 260, flex: 1 },
    { headerName: 'Drp1', field: 'drp1', cellRenderer: makeDrpAdornmentCell('drp1', 'Drp1'), width: 180 },
    { headerName: 'Drp2', field: 'drp2', cellRenderer: makeDrpAdornmentCell('drp2', 'Drp2'), width: 180 },
    { headerName: 'Fibre', field: 'fibre', cellType: 'select', cellProps: { options: FIBRE_OPTIONS }, width: 160 },
    { headerName: 'Licensor', field: 'licensorText', cellRenderer: LicensorInputCell, width: 200 },
    { headerName: 'Price', field: 'unitCost', cellRenderer: PriceAdornmentCell, width: 140 },
    { headerName: 'Discount', field: 'discount', cellRenderer: DiscountAdornmentCell, width: 140 },
    { headerName: 'Exclude', field: 'exclude', cellType: 'checkbox', width: 110 },
    { headerName: 'Created', field: 'created', cellType: 'readonlyText', cellProps: { fallback: '2026-05-01' }, width: 140 },
    { headerName: 'Action', field: 'action', cellRenderer: DeleteActionCell, width: 90, pinned: 'right', lockPinned: true, suppressMenu: true },
  ];
  return (
    <div className="container mt-4 pb-5">
      <h2 className="mb-4">Bombiix Master Design</h2>

      {/* ── Icon-container buttons — all modifiers × all common icons ─── */}
      <section className="mb-5">
        <h4 className="mb-3">Icon-container buttons</h4>
        <p className="text-muted">
          20×20 wrapper, 16×16 glyph, 4px radius. Hover to see each modifier’s colour scheme. <code>--green</code> and <code>--inactive</code> are permanent (no
          hover shift).
        </p>

        {/* Row 1 — every modifier paired with the `add` glyph */}
        <div className="mb-3">
          <div className="small text-uppercase text-bold mb-2">Modifiers (with `add` icon) — bg / icon colours</div>
          <div className="d-flex flex-wrap align-items-start" style={{ gap: 16 }}>
            {[
              { mod: '', label: 'default', bg: '#c2e5fb', fg: '#6dc1f5' },
              { mod: '--blue', label: 'blue', bg: '#c2e5fb', fg: '#6dc1f5' },
              { mod: '--red', label: 'red', bg: '#fee5e6', fg: '#fa979b' },
              { mod: '--dark', label: 'dark', bg: '#3c3c3c', fg: '#ffffff' },
              { mod: '--amber', label: 'amber', bg: '#fff4e5', fg: '#fdd499' },
              { mod: '--purple', label: 'purple', bg: '#ecdbff', fg: '#d1aaff' },
              { mod: '--khaki', label: 'khaki (permanent)', bg: '#f9f5ee', fg: '#e0cbaa' },
              { mod: '--green', label: 'green (permanent)', bg: '#dcf0eb', fg: '#73c5af' },
              { mod: '--inactive', label: 'inactive (permanent)', bg: '#fee5e6', fg: '#fa979b' },
            ].map(({ mod, label, bg, fg }) => (
              <div key={label} className="text-center" style={{ minWidth: 110 }}>
                <button type="button" className={`bombiix-icon-container bombiix-icon-container${mod}`} title={label} aria-label={label}>
                  <AddIcon />
                </button>
                <div className="small mt-1 text-bold">{label}</div>
                <div className="d-flex align-items-center justify-content-center mt-1" style={{ gap: 4 }}>
                  <span
                    aria-label={`bg ${bg}`}
                    title={`bg ${bg}`}
                    style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: bg, border: '1px solid #d6d6d6' }}
                  />
                  <code className="user-select-all" style={{ fontSize: 11 }}>
                    {bg}
                  </code>
                </div>
                <div className="d-flex align-items-center justify-content-center mt-1" style={{ gap: 4 }}>
                  <span
                    aria-label={`icon ${fg}`}
                    title={`icon ${fg}`}
                    style={{ display: 'inline-block', width: 12, height: 12, borderRadius: 2, background: fg, border: '1px solid #d6d6d6' }}
                  />
                  <code className="user-select-all" style={{ fontSize: 11 }}>
                    {fg}
                  </code>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Row 1b — SVG icons (one TSX per icon) driven by the same modifiers */}
        <div className="mb-3">
          <div className="small text-uppercase text-bold mb-2">
            SVG icons — <code>src/bombiix-icon/</code> TSX components
          </div>
          <p className="small text-muted mb-2">
            One-file-per-icon SVG components. Colour inheritance via <code>currentColor</code> — same 8 modifiers apply with zero extra CSS.
          </p>

          {/* Row A: all icons at default (no modifier) */}
          <div className="d-flex flex-wrap align-items-end mb-2" style={{ gap: 16 }}>
            {[
              { Icon: EditIcon, label: 'Edit', name: 'EditIcon' },
              { Icon: DownloadIcon, label: 'Download', name: 'DownloadIcon' },
              { Icon: ProductGroupIcon, label: 'Product Group', name: 'ProductGroupIcon' },
              { Icon: SummaryIcon, label: 'Summary', name: 'SummaryIcon' },
            ].map(({ Icon, label, name }) => (
              <div key={name} className="text-center" style={{ minWidth: 110 }}>
                <button type="button" className="bombiix-icon-container" aria-label={label}>
                  <Icon size={20} />
                </button>
                <div className="small mt-1 text-bold">{label}</div>
                <code style={{ fontSize: 10 }}>{name}</code>
              </div>
            ))}
          </div>

          {/* Row B: size prop (`size` accepts number or string) */}
          <div className="small text-uppercase text-muted mb-2" style={{ letterSpacing: 0.5 }}>
            EditIcon × <code>size</code> prop
          </div>
          <div className="d-flex flex-wrap align-items-end mb-2" style={{ gap: 16 }}>
            {[20, 24, 28, 32, 36, 40].map((sizePx) => (
              <div key={sizePx} className="text-center" style={{ minWidth: 72 }}>
                <button type="button" className="bombiix-icon-container" aria-label={`size ${sizePx}`}>
                  <EditIcon size={sizePx} />
                </button>
                <div className="small mt-1">{sizePx}px</div>
              </div>
            ))}
          </div>

          {/* Row C: EditIcon cycled through every modifier */}
          <div className="small text-uppercase text-muted mb-2" style={{ letterSpacing: 0.5 }}>
            EditIcon × every modifier
          </div>
          <div className="d-flex flex-wrap align-items-end" style={{ gap: 16 }}>
            {[
              { mod: '', label: 'default' },
              { mod: '--blue', label: 'blue' },
              { mod: '--red', label: 'red' },
              { mod: '--dark', label: 'dark' },
              { mod: '--amber', label: 'amber' },
              { mod: '--purple', label: 'purple' },
              { mod: '--khaki', label: 'khaki (permanent)' },
              { mod: '--green', label: 'green (permanent)' },
              { mod: '--inactive', label: 'inactive (permanent)' },
            ].map(({ mod, label }) => (
              <div key={label} className="text-center" style={{ minWidth: 96 }}>
                <button type="button" className={`bombiix-icon-container bombiix-icon-container${mod}`} aria-label={label}>
                  <CloseIcon size={28} />
                </button>
                <div className="small mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Row 2 — common action icons paired with a semantically-matching modifier */}
        <div className="mb-3">
          <div className="small text-uppercase text-bold mb-2">Common action pairings</div>
          <div className="d-flex flex-wrap align-items-end" style={{ gap: 16 }}>
            {[
              { Icon: AddIcon, mod: '--blue', label: 'Add' },
              { Icon: EditIcon, mod: '--blue', label: 'Edit' },
              { Icon: DeleteIcon, mod: '--red', label: 'Delete' },
              { Icon: ArchiveIcon, mod: '--dark', label: 'Archive' },
              { Icon: ActiveIcon, mod: '--green', label: 'Approve' },
              { Icon: CloseIcon, mod: '--red', label: 'Close' },
              { Icon: DownloadIcon, mod: '--blue', label: 'Download' },
              { Icon: DuplicateIcon, mod: '--blue', label: 'Duplicate' },
              { Icon: FilterIcon, mod: '--purple', label: 'Filter' },
            ].map(({ Icon, mod, label }) => (
              <div key={label} className="text-center" style={{ minWidth: 72 }}>
                <button type="button" className={`bombiix-icon-container bombiix-icon-container${mod}`} title={label} aria-label={label}>
                  <Icon />
                </button>
                <div className="small mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Row 2b — same pairings, rendered at f-20 for emphasis/toolbar usage */}
        <div className="mb-3">
          <div className="small text-uppercase text-bold mb-2">Common action pairings (f-20)</div>
          <div className="d-flex flex-wrap align-items-end" style={{ gap: 16 }}>
            {[
              { Icon: AddIcon, mod: '--blue', label: 'Add' },
              { Icon: EditIcon, mod: '--blue', label: 'Edit' },
              { Icon: DeleteIcon, mod: '--red', label: 'Delete' },
              { Icon: ArchiveIcon, mod: '--dark', label: 'Archive' },
              { Icon: ActiveIcon, mod: '--green', label: 'Approve' },
              { Icon: CloseIcon, mod: '--red', label: 'Close' },
              { Icon: DownloadIcon, mod: '--blue', label: 'Download' },
              { Icon: DuplicateIcon, mod: '--blue', label: 'Duplicate' },
              { Icon: FilterIcon, mod: '--purple', label: 'Filter' },
            ].map(({ Icon, mod, label }) => (
              <div key={label} className="text-center" style={{ minWidth: 72 }}>
                <button type="button" className={`bombiix-icon-container bombiix-icon-container${mod}`} title={label} aria-label={label}>
                  <Icon size={20} />
                </button>
                <div className="small mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Row 3 — glyph-size utility (f-12 / f-14 / f-16 / f-20 / f-22 / f-24) sanity check */}
        <div className="mb-3">
          <div className="small text-uppercase text-bold mb-2">Glyph sizes (`f-*` utility)</div>
          <div className="d-flex flex-wrap align-items-end" style={{ gap: 16 }}>
            {[
              { label: 'f-12', px: 12 },
              { label: 'f-14', px: 14 },
              { label: 'f-16', px: 16 },
              { label: 'f-20', px: 20 },
              { label: 'f-22', px: 22 },
              { label: 'f-24', px: 24 },
            ].map(({ label, px }) => (
              <div key={label} className="text-center" style={{ minWidth: 60 }}>
                <button type="button" className="bombiix-icon-container" aria-label={label}>
                  <EditIcon size={px} />
                </button>
                <div className="small mt-1">{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Row 3b — .active state (locks default-blue hover appearance) */}
        <div className="mb-3">
          <div className="small text-uppercase text-bold mb-2">Active state — `.active` (permanent blue, e.g. filter applied)</div>
          <div className="d-flex flex-wrap align-items-end" style={{ gap: 16 }}>
            <div className="text-center" style={{ minWidth: 90 }}>
              <button type="button" className="bombiix-icon-container" aria-label="Filter (off)">
                <FilterIcon />
              </button>
              <div className="small mt-1">idle</div>
            </div>
            <div className="text-center" style={{ minWidth: 90 }}>
              <button type="button" className="bombiix-icon-container active" aria-label="Filter (applied)">
                <FilterIcon />
              </button>
              <div className="small mt-1">.active (filter applied)</div>
            </div>
            <div className="text-center" style={{ minWidth: 90 }}>
              <button type="button" className="bombiix-icon-container active" aria-label="Sort (applied)">
                <SortIcon />
              </button>
              <div className="small mt-1">.active (sort)</div>
            </div>
          </div>
        </div>

        {/* Row 4 — disabled + inline row-action pattern */}
        <div className="mb-3">
          <div className="small text-uppercase text-bold mb-2">Row-action pattern (edit + delete inline)</div>
          <div className="d-inline-flex align-items-center" style={{ gap: 6 }}>
            <button type="button" className="bombiix-icon-container bombiix-icon-container--blue" aria-label="Edit">
              <EditIcon />
            </button>
            <button type="button" className="bombiix-icon-container bombiix-icon-container--red" aria-label="Delete">
              <DeleteIcon />
            </button>
            <button type="button" className="bombiix-icon-container" aria-label="Disabled" disabled>
              <AddIcon />
            </button>
            <span className="small text-muted ml-2">(last one is disabled)</span>
          </div>
        </div>

        {/* Row 5 — .icons-container group wrapper with gap-4 / gap-8 */}
        <div>
          <div className="small text-uppercase text-bold mb-2">Group wrapper — `.icons-container` with `.gap-4` / `.gap-8`</div>

          <div className="mb-2">
            <span className="small text-muted mr-2">gap-4:</span>
            <div className="icons-container gap-4">
              <button type="button" className="bombiix-icon-container bombiix-icon-container--blue" aria-label="Edit">
                <EditIcon />
              </button>
              <button type="button" className="bombiix-icon-container bombiix-icon-container--blue" aria-label="Duplicate">
                <DuplicateIcon />
              </button>
              <button type="button" className="bombiix-icon-container bombiix-icon-container--red" aria-label="Delete">
                <DeleteIcon />
              </button>
            </div>
          </div>

          <div>
            <span className="small text-muted mr-2">gap-8:</span>
            <div className="icons-container gap-8">
              <button type="button" className="bombiix-icon-container bombiix-icon-container--dark" aria-label="Archive">
                <ArchiveIcon />
              </button>
              <button type="button" className="bombiix-icon-container bombiix-icon-container--blue" aria-label="Search">
                <SearchIcon />
              </button>
              <button type="button" className="bombiix-icon-container bombiix-icon-container--amber" aria-label="Publish">
                <PublishIcon />
              </button>
              <button type="button" className="bombiix-icon-container bombiix-icon-container--purple" aria-label="Filter">
                <FilterIcon />
              </button>
            </div>
          </div>

          <div className="mt-3">
            <span className="small text-muted mr-2">new icons:</span>
            <div className="icons-container gap-8">
              <button type="button" className="bombiix-icon-container" aria-label="Pencil">
                <PencilIcon />
              </button>
              <button type="button" className="bombiix-icon-container" aria-label="Calendar">
                <CalendarIcon2 />
              </button>
              <button type="button" className="bombiix-icon-container" aria-label="Face">
                <FaceIcon />
              </button>
              <button type="button" className="bombiix-icon-container" aria-label="Globe">
                <GlobeIcon />
              </button>
              <button type="button" className="bombiix-icon-container" aria-label="Presentation">
                <PresentationIcon />
              </button>
              <button type="button" className="bombiix-icon-container" aria-label="Camera">
                <CameraIcon />
              </button>
              <button type="button" className="bombiix-icon-container" aria-label="Weather">
                <WeatherIcon />
              </button>
              <button type="button" className="bombiix-icon-container" aria-label="Test report">
                <TestReportIcon />
              </button>
              <button type="button" className="bombiix-icon-container" aria-label="File search">
                <FileSearchIcon />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Floaty — dark floating action toolbar (<Floaty />) ─────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Floaty — .bombiix-floaty</h4>
        <p className="text-muted">
          Dark floating action toolbar (Figma node <code>10614:341551</code>). <code>Floaty</code> is a styled dark-bar container — compose it from{' '}
          <code>FloatyButton</code>s for plain actions, or wrap a <code>FloatyButton</code> in an <code>OverlayTrigger</code> / <code>CommonDropdown</code> when
          a slot needs a popover or dropdown. <code>FloatyButton</code> forwards its ref, so it works directly as an overlay/dropdown trigger, and shows the
          shared <code>CustomTooltip</code> on hover (pass <code>{'showTooltip={false}'}</code> when it already sits inside an overlay trigger). Un-modified
          glyphs render white against the dark bar; <code>variant</code> buttons keep their own colours (khaki view toggle, red delete).
        </p>
        <p className="small text-muted mb-3">
          <strong>Positioning</strong> — pass <code>position</code>: <code>fixed</code> pins the bar horizontally centred, 24px from the bottom of the viewport;{' '}
          <code>fixed-raised</code> lifts it to 74px (50px approval-bar height + 24px) so it clears an open approve/reject bar. Default is <code>inline</code> (all
          examples below render in normal flow).
        </p>

        <div className="small text-uppercase text-bold mb-2">Plain buttons</div>
        <Floaty>
          <FloatyButton icon={AddIcon} label="Add" onClick={() => {}} />
          <FloatyButton icon={DownloadIcon} label="Download" onClick={() => {}} />
          <FloatyButton icon={ShareIcon} label="Share" onClick={() => {}} />
          <FloatyButton icon={ArchiveIcon} label="Archive" onClick={() => {}} />
          <FloatyButton icon={DeleteIcon} label="Delete" variant="red" onClick={() => {}} />
          <FloatyButton icon={TableViewIcon} label="Toggle view" variant="khaki" onClick={() => {}} />
        </Floaty>

        <div className="small text-uppercase text-bold mb-2 mt-4">
          Grouped common actions — <code>FloatyGroup</code>
        </div>
        <p className="small text-muted mb-2">
          Wrap a cluster of common actions in <code>FloatyGroup</code> to club them into one shared <code>#444</code> &quot;glass&quot; well with a single 8px
          radius and one inset rim (Figma node <code>10614:341498</code>). Pass <code>disabled</code> on the group to grey the whole cluster to the muted{' '}
          <code>#A4A4A4</code> glyph in one place — the standalone <strong>Add</strong> and khaki <strong>view</strong> buttons around it stay active.
        </p>
        <Floaty>
          <FloatyButton icon={AddIcon} label="Add" onClick={() => {}} />
          <FloatyGroup disabled>
            <FloatyButton icon={DownloadIcon} label="Download" onClick={() => {}} />
            <FloatyButton icon={ShareIcon} label="Share" onClick={() => {}} />
            <FloatyButton icon={ArchiveIcon} label="Archive" onClick={() => {}} />
            <FloatyButton icon={DeleteIcon} label="Delete" onClick={() => {}} />
          </FloatyGroup>
          <FloatyButton icon={TableViewIcon} label="Toggle view" variant="khaki" onClick={() => {}} />
        </Floaty>

        <p className="small text-muted mb-2 mt-3">
          Same <code>FloatyGroup</code> with all icons enabled — the <code>bombiix-floaty-group--flat</code> modifier (added automatically when the group is not{' '}
          <code>disabled</code>) drops the well background and shadow, so the cluster reads flat like the rest of the bar:
        </p>
        <Floaty>
          <FloatyButton icon={AddIcon} label="Add" onClick={() => {}} />
          <FloatyGroup>
            <FloatyButton icon={DownloadIcon} label="Download" onClick={() => {}} />
            <FloatyButton icon={ShareIcon} label="Share" onClick={() => {}} />
            <FloatyButton icon={ArchiveIcon} label="Archive" onClick={() => {}} />
            <FloatyButton icon={DeleteIcon} label="Delete" onClick={() => {}} />
          </FloatyGroup>
          <FloatyButton icon={TableViewIcon} label="Toggle view" variant="khaki" onClick={() => {}} />
        </Floaty>

        <div className="small text-uppercase text-bold mb-2 mt-4">Popover &amp; dropdown triggers</div>
        <p className="small text-muted mb-2">
          A slot can open a popover or menu instead of firing a click — wrap a <code>FloatyButton</code> in an <code>OverlayTrigger</code>. Here the{' '}
          <strong>Product Groups</strong> button opens a selectable list and <strong>Submit</strong> opens a small action menu, both built with the shared
          <code>Popover</code>.
        </p>
        <Floaty>
          <FloatyButton icon={AddIcon} label="Add" onClick={() => {}} />
          <OverlayTrigger
            placement="top"
            trigger="click"
            rootClose
            overlay={
              <Popover id="md-floaty-groups" className="bombiix-info-popover">
                <Popover.Content>
                  <div className="d-flex flex-column" style={{ gap: 4, minWidth: 140 }}>
                    {['GTEST', '54534', '123', 'Testing', 'Clothing'].map((group) => (
                      <button key={group} type="button" className="bombiix-btn btn-light text-left">
                        {group}
                      </button>
                    ))}
                  </div>
                </Popover.Content>
              </Popover>
            }
          >
            <FloatyButton icon={GroupIcon} label="Product Groups" showTooltip={false} />
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            trigger="click"
            rootClose
            overlay={
              <Popover id="md-floaty-submit" className="bombiix-info-popover">
                <Popover.Content>
                  <div className="d-flex flex-column" style={{ gap: 4, minWidth: 120 }}>
                    <button type="button" className="bombiix-btn btn-light text-left">
                      Submit
                    </button>
                    <button type="button" className="bombiix-btn btn-light text-left">
                      Download
                    </button>
                  </div>
                </Popover.Content>
              </Popover>
            }
          >
            <FloatyButton icon={SubmitAndDownloadIcon} label="Submit" showTooltip={false} />
          </OverlayTrigger>
          <FloatyButton icon={TableViewIcon} label="Toggle view" variant="khaki" onClick={() => {}} />
        </Floaty>
      </section>

      {/* ── Primary button — .bombiix-btn ─────────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Primary button — .bombiix-btn</h4>
        <p className="text-muted">
          Normal → drop shadow. Hover → glow shadow (blur only, no offset). Press → inset light-top / dark-bottom shadows (reads as pressed-in).
        </p>

        <div className="d-flex flex-wrap align-items-center" style={{ gap: 16 }}>
          <div className="text-center">
            <button type="button" className="bombiix-btn btn-blue">
              Save
            </button>
            <div className="small mt-1 text-muted">idle</div>
          </div>
          <div className="text-center">
            <button type="button" className="bombiix-btn btn-light">
              Back
            </button>
            <div className="small mt-1 text-muted">idle</div>
          </div>

          <div className="text-center">
            <button type="button" className="bombiix-btn active ">
              Save
            </button>
            <div className="small mt-1 text-muted">pressed (.active)</div>
          </div>

          <div className="text-center">
            <button type="button" className="bombiix-btn  d-inline-flex align-items-center">
              <AddIcon className="mr-2" />
              Add row
            </button>
            <div className="small mt-1 text-muted">icon + label</div>
          </div>

          <div className="text-center">
            <button type="button" className="bombiix-btn btn-blue" disabled>
              Disabled
            </button>
            <div className="small mt-1 text-muted">disabled (overrides any variant)</div>
          </div>

          <div className="text-center">
            <button type="button" className="bombiix-btn btn-red">
              Delete
            </button>
            <div className="small mt-1 text-muted">.btn-red (destructive)</div>
          </div>

          <div className="text-center">
            <button type="button" className="bombiix-btn btn-dark d-inline-flex align-items-center">
              Icon Right
              <EmoteIcon size={16} className="ml-2" />
            </button>
            <div className="small mt-1 text-muted">.btn-dark (icon right)</div>
          </div>

          <div className="text-center">
            <button type="button" className="bombiix-btn btn-dark">
              Bombiix Black
            </button>
            <div className="small mt-1 text-muted">.btn-dark</div>
          </div>

          <div className="text-center">
            <button type="button" className="bombiix-btn btn-khaki">
              Groups
            </button>
            <div className="small mt-1 text-muted">.btn-khaki</div>
          </div>

          <div className="text-center">
            <button type="button" className="bombiix-btn btn-teal">
              Library
            </button>
            <div className="small mt-1 text-muted">.btn-teal</div>
          </div>
        </div>
      </section>

      {/* ── Title-bar buttons — .bombiix-btn + .btn-blue / .btn-light ──── */}
      <section className="mb-5">
        <h4 className="mb-3">Title-bar buttons (.bombiix-btn + .btn-blue / .btn-light)</h4>
        <p className="text-muted">
          <code>.bombiix-btn</code> is colour-agnostic shadow scaffolding. Pair with <code>.btn-blue</code> (primary — Save) or
          <code>.btn-light</code> (secondary — Back / Cancel) for fill.
        </p>
        <div className="page-title-filter">
          <div className="title-container">
            <div className="title">Preview</div>
          </div>
          <div className="search-filter">
            <div style={{ width: 200 }} className="mr-8">
              <NewSearch className="bombiix-search" value={searchValue} onChange={setSearchValue} placeholder="Search" testId="master-design-titlebar-search" />
            </div>
            <button type="button" className="bombiix-btn btn-blue ml-8">
              Save
            </button>
            <button type="button" className="bombiix-btn btn-light ml-8">
              Back
            </button>
          </div>
        </div>
      </section>

      {/* ── Search input — .bombiix-search (uses <NewSearch />) ────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Search input — .bombiix-search</h4>
        <p className="text-muted">
          28px height, 4px radius, 1px <code>#A4A4A4</code> border, 10px horizontal padding, 14px AktivGrotesk-Medium <code>#3C3C3C</code> text, search glyph on
          the right. Rendered via the shared <code>NewSearch</code> component with
          <code>className=&quot;bombiix-search&quot;</code>.
        </p>

        <div className="d-flex flex-wrap align-items-start" style={{ gap: 24 }}>
          <div style={{ width: 200 }}>
            <NewSearch className="bombiix-search" value={searchValue} onChange={setSearchValue} placeholder="Search" testId="master-design-search-idle" />
            <div className="small mt-1 text-muted">idle / focus (click to see)</div>
          </div>

          <div style={{ width: 200 }}>
            <NewSearch className="bombiix-search" value={searchFilled} onChange={setSearchFilled} placeholder="Search" testId="master-design-search-filled" />
            <div className="small mt-1 text-muted">with value (shows clear)</div>
          </div>

          <div style={{ width: 200 }}>
            <NewSearch className="bombiix-search is-disabled" value="" onChange={() => {}} placeholder="Search" testId="master-design-search-disabled" />
            <div className="small mt-1 text-muted">disabled (.is-disabled)</div>
          </div>

          <div style={{ width: 200 }}>
            <NewSearch
              className="bombiix-search"
              value={searchOnEnterValue}
              onChange={setSearchOnEnterValue}
              placeholder="Search"
              searchOnEnter
              testId="master-design-search-on-enter"
            />
            <div className="small mt-1 text-muted">
              searchOnEnter — fires on Enter (committed: <code>{searchOnEnterValue || '—'}</code>)
            </div>
          </div>
        </div>
      </section>

      {/* ── Text input — <NewInput /> (.bombiix-input SCSS) ───────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Text input — NewInput</h4>
        <p className="text-muted">
          Sourced from <code>Components/PIM/common/NewInput</code>. Styling driven by
          <code> _input.scss</code>. Pass <code>label</code> prop to render a caption above the field.
        </p>

        <div className="small text-uppercase text-bold mb-2">States</div>
        <div className="d-flex flex-wrap align-items-start mb-3" style={{ gap: 24 }}>
          <div style={{ width: 200 }}>
            <NewInput placeholder="Text Box" value={inputValue} onChange={(e) => setInputValue(e.target.value)} />
            <div className="small mt-1 text-muted">idle</div>
          </div>

          <div style={{ width: 200 }}>
            <NewInput placeholder="Text Box" value={inputFilled} onChange={(e) => setInputFilled(e.target.value)} />
            <div className="small mt-1 text-muted">with value</div>
          </div>

          <div style={{ width: 200 }}>
            <NewInput placeholder="Text Box" defaultValue="wrong" invalid />
            <div className="small mt-1 text-muted">invalid</div>
          </div>

          <div style={{ width: 200 }}>
            <NewInput placeholder="Text Box" disabled />
            <div className="small mt-1 text-muted">disabled</div>
          </div>

          <div style={{ width: 200 }}>
            <NewInput placeholder="Text Box" defaultValue="Highlighted" isHighlight />
            <div className="small mt-1 text-muted">highlight — `isHighlight`</div>
          </div>
        </div>

        <div className="small text-uppercase text-bold mb-2">With label — `label` prop</div>
        <div className="d-flex flex-wrap align-items-start" style={{ gap: 24 }}>
          <div style={{ width: 200 }}>
            <NewInput label="Reference" placeholder="Text Box" />
            <div className="small mt-1 text-muted">idle + label</div>
          </div>

          <div style={{ width: 200 }}>
            <NewInput label="Reference" placeholder="Text Box" defaultValue="SMVR-001" />
            <div className="small mt-1 text-muted">value + label</div>
          </div>

          <div style={{ width: 200 }}>
            <NewInput label="Required Field" placeholder="Text Box" defaultValue="wrong" invalid />
            <div className="small mt-1 text-muted">invalid + label</div>
          </div>

          <div style={{ width: 200 }}>
            <NewInput label="Reference" placeholder="Text Box" disabled />
            <div className="small mt-1 text-muted">disabled + label</div>
          </div>

          <div style={{ width: 200 }}>
            <NewInput label="Product Number" placeholder="Text Box" required />
            <div className="small mt-1 text-muted">required + label</div>
          </div>
        </div>

        <div className="small text-uppercase text-bold mt-4 mb-2">
          With adornments — <code>startAdornment</code> / <code>endAdornment</code>
        </div>
        <p className="small text-muted mb-2">
          Any ReactNode fits into either slot. Pair with <code>InputAdornmentButton</code> for icon buttons, <code>NewCheckbox</code> for selection, the shared{' '}
          <code>.design-switch</code> markup for toggles, or plain glyph text (£, %) for static prefixes/suffixes. The whole field lights up on focus.
        </p>

        <div className="d-flex flex-wrap align-items-start" style={{ gap: 16 }}>
          <div style={{ width: 240 }}>
            <NewInput placeholder="Text Box" startAdornment={<InputAdornmentButton icon={AddIcon} ariaLabel="Add" onClick={() => {}} />} />
            <div className="small mt-1 text-muted">+ prefix button</div>
          </div>

          <div style={{ width: 240 }}>
            <NewInput
              placeholder="Text Box"
              startAdornment={<InputAdornmentButton icon={DeleteIcon} ariaLabel="Delete" variant="danger" onClick={() => {}} />}
            />
            <div className="small mt-1 text-muted">trash prefix button</div>
          </div>

          <div style={{ width: 260 }}>
            <NewInput
              placeholder="Text Box"
              startAdornment={
                <>
                  <InputAdornmentButton icon={AddIcon} ariaLabel="Add" onClick={() => {}} />
                  <InputAdornmentButton icon={DeleteIcon} ariaLabel="Delete" variant="danger" onClick={() => {}} />
                </>
              }
            />
            <div className="small mt-1 text-muted">+ &amp; trash together</div>
          </div>

          <div style={{ width: 200 }}>
            <NewInput placeholder="0.00" startAdornment="£" />
            <div className="small mt-1 text-muted">£ currency prefix</div>
          </div>

          <div style={{ width: 240 }}>
            <NewInput
              placeholder="Text Box"
              value={switchOnValue}
              onChange={(e) => setSwitchOnValue(e.target.value)}
              startAdornment={
                <div className="design-switch d-inline-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input mt-0 comment-switch-input"
                    id="md-switch-on"
                    name="md-switch-on"
                    checked={switchOn}
                    onChange={(e) => setSwitchOn(e.target.checked)}
                    aria-label="Toggle"
                  />
                </div>
              }
            />
            <div className="small mt-1 text-muted">toggle switch (on)</div>
          </div>

          <div style={{ width: 240 }}>
            <NewInput
              placeholder="Text Box"
              value={switchOffValue}
              onChange={(e) => setSwitchOffValue(e.target.value)}
              startAdornment={
                <div className="design-switch d-inline-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input mt-0 comment-switch-input"
                    id="md-switch-off"
                    name="md-switch-off"
                    checked={switchOff}
                    onChange={(e) => setSwitchOff(e.target.checked)}
                    aria-label="Toggle"
                  />
                </div>
              }
            />
            <div className="small mt-1 text-muted">toggle switch (off)</div>
          </div>

          <div style={{ width: 200 }}>
            <NewInput placeholder="0" endAdornment="%" />
            <div className="small mt-1 text-muted">% suffix</div>
          </div>

          <div style={{ width: 300 }}>
            <NewInput
              placeholder="Product Description"
              value={descValue}
              maxLength={200}
              onChange={(e) => setDescValue(e.target.value)}
              startAdornment={<NewCheckbox size="sm" checked={descSelected} onChange={(e) => setDescSelected(e.target.checked)} />}
              endAdornment={<span className="bombiix-input__counter">{descValue.length}/200</span>}
            />
            <div className="small mt-1 text-muted">checkbox prefix + counter suffix</div>
          </div>

          <div style={{ width: 260 }}>
            <NewInput placeholder="Text Box" endAdornment={<InputAdornmentButton icon={AddIcon} ariaLabel="Add option" onClick={() => {}} />} />
            <div className="small mt-1 text-muted">+ suffix add-button</div>
          </div>
        </div>

        <p className="small text-muted mt-4 mb-0">
          Every adornment variation is also wired into <strong>Editable tree grid — product with colours</strong> below, so you can see the same primitives
          running inside a real <code>BombiixAGGrid</code> — checkbox+counter (Description), <code>+</code> add-button (Drp1/Drp2), <code>.design-switch</code>{' '}
          inside input (Licensor), <code>£</code> prefix (Price), <code>%</code> suffix (Discount), and a destructive delete (Action).
        </p>
      </section>

      {/* ── Text area — <NewTextArea /> (.bombiix-textarea SCSS) ─────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Text area — NewTextArea</h4>
        <p className="text-muted">
          Sourced from <code>Components/PIM/common/NewTextArea</code>. Same chrome as <code>NewInput</code> (border, focus, invalid, disabled) but multi-line
          with vertical resize. Pass <code>rows</code> to change the initial height.
        </p>

        <div className="d-flex flex-wrap align-items-start" style={{ gap: 16 }}>
          <div style={{ width: 260 }}>
            <NewTextArea placeholder="Notes" rows={2} resize="vertical" />
            <div className="small mt-1 text-muted">idle · rows=2 · resize=vertical</div>
          </div>

          <div style={{ width: 260 }}>
            <NewTextArea placeholder="Notes" rows={4} resize="vertical" defaultValue={'Line one\nLine two'} />
            <div className="small mt-1 text-muted">with value</div>
          </div>

          <div style={{ width: 260 }}>
            <NewTextArea placeholder="Notes" rows={4} resize="vertical" defaultValue="Something bad happened" invalid />
            <div className="small mt-1 text-muted">invalid</div>
          </div>

          <div style={{ width: 260 }}>
            <NewTextArea placeholder="Notes" rows={4} resize="vertical" disabled />
            <div className="small mt-1 text-muted">disabled</div>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-start mt-3" style={{ gap: 16 }}>
          <div style={{ width: 260 }}>
            <NewTextArea label="Notes" placeholder="Add a description" rows={4} resize="vertical" />
            <div className="small mt-1 text-muted">idle + label</div>
          </div>

          <div style={{ width: 260 }}>
            <NewTextArea label="Notes" placeholder="Add a description" rows={4} resize="none" defaultValue="Cotton lining, breathable." />
            <div className="small mt-1 text-muted">value + label · resize=none</div>
          </div>

          <div style={{ width: 260 }}>
            <NewTextArea label="Required Notes" placeholder="Add a description" rows={4} resize="vertical" defaultValue="Missing details" invalid />
            <div className="small mt-1 text-muted">invalid + label</div>
          </div>

          <div style={{ width: 260 }}>
            <NewTextArea label="Notes" placeholder="Add a description" rows={4} resize="vertical" disabled />
            <div className="small mt-1 text-muted">disabled + label</div>
          </div>
        </div>
      </section>

      {/* ── Select / MultiSelect — <NewSelectMultiSelect /> ──────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Select / MultiSelect — NewSelectMultiSelect</h4>
        <p className="text-muted">
          Sourced from <code>Components/PIM/common/NewSelectMultiSelect</code>. Single-select via default, multi-select via <code>isMulti</code>. Chrome (wrap,
          label, alert) driven by <code>_select.scss</code>; react-select internals use JS overrides (v4 emotion). Pass <code>label</code> to render a caption
          above the field.
        </p>

        <div className="small text-uppercase text-bold mb-2">States</div>
        <div className="d-flex flex-wrap align-items-start mb-3" style={{ gap: 24 }}>
          <div style={{ width: 240 }}>
            <NewSelectMultiSelect options={FIBRE_OPTIONS} value={singleSelect} onChange={setSingleSelect} placeholder="Select fibre" />
            <div className="small mt-1 text-muted">single-select (idle)</div>
          </div>

          <div style={{ width: 240 }}>
            <NewSelectMultiSelect options={FIBRE_OPTIONS} value={selectClearable} onChange={setSelectClearable} placeholder="Select fibre" isClearable />
            <div className="small mt-1 text-muted">single with value + clearable</div>
          </div>

          <div style={{ width: 240 }}>
            <NewSelectMultiSelect options={FIBRE_OPTIONS} value={multiSelect} onChange={setMultiSelect} placeholder="Select fibres" isMulti />
            <div className="small mt-1 text-muted">multi-select</div>
          </div>

          <div style={{ width: 240 }}>
            <NewSelectMultiSelect options={FIBRE_OPTIONS} value={selectLabelInvalid} onChange={setSelectLabelInvalid} placeholder="Select fibre" invalid />
            <div className="small mt-1 text-muted">invalid</div>
          </div>

          <div style={{ width: 240 }}>
            <NewSelectMultiSelect options={FIBRE_OPTIONS} value={null} onChange={() => {}} placeholder="Disabled" isDisabled />
            <div className="small mt-1 text-muted">disabled</div>
          </div>

          <div style={{ width: 240 }}>
            <NewSelectMultiSelect options={FIBRE_OPTIONS} value={selectClearable} onChange={setSelectClearable} placeholder="Select fibre" isHighlight />
            <div className="small mt-1 text-muted">highlight — `isHighlight`</div>
          </div>
        </div>

        <div className="small text-uppercase text-bold mb-2">With label — `label` prop</div>
        <div className="d-flex flex-wrap align-items-start" style={{ gap: 24 }}>
          <div style={{ width: 240 }}>
            <NewSelectMultiSelect label="Fibre" options={FIBRE_OPTIONS} value={selectLabelIdle} onChange={setSelectLabelIdle} placeholder="Select fibre" />
            <div className="small mt-1 text-muted">idle + label</div>
          </div>

          <div style={{ width: 240 }}>
            <NewSelectMultiSelect
              label="Fibre"
              options={FIBRE_OPTIONS}
              value={selectLabelValue}
              onChange={setSelectLabelValue}
              placeholder="Select fibre"
              isClearable
            />
            <div className="small mt-1 text-muted">value + label</div>
          </div>

          <div style={{ width: 240 }}>
            <NewSelectMultiSelect
              label="Fibre"
              options={FIBRE_OPTIONS}
              value={selectLabelMulti}
              onChange={setSelectLabelMulti}
              placeholder="Select fibres"
              isMulti
            />
            <div className="small mt-1 text-muted">multi + label</div>
          </div>

          <div style={{ width: 240 }}>
            <NewSelectMultiSelect
              label="Required Fibre"
              options={FIBRE_OPTIONS}
              value={selectLabelInvalid}
              onChange={setSelectLabelInvalid}
              placeholder="Select fibre"
              invalid
            />
            <div className="small mt-1 text-muted">invalid + label</div>
          </div>

          <div style={{ width: 240 }}>
            <NewSelectMultiSelect label="Fibre" options={FIBRE_OPTIONS} value={null} onChange={() => {}} placeholder="Disabled" isDisabled />
            <div className="small mt-1 text-muted">disabled + label</div>
          </div>

          <div style={{ width: 240 }}>
            <NewSelectMultiSelect
              label="Brand"
              options={FIBRE_OPTIONS}
              value={selectLabelIdle}
              onChange={setSelectLabelIdle}
              placeholder="Select brand"
              required
            />
            <div className="small mt-1 text-muted">required + label</div>
          </div>
        </div>

        <div className="small text-uppercase text-bold mt-4 mb-2">
          Menu extras — <code>showSearch</code>, <code>selectAllLabel</code>, <code>clearAllLabel</code>
        </div>
        <p className="small text-muted mb-2">
          Toggle a search field at the top of the menu, a Select All row, and/or a Clear All footer button — each is optional and additive.
        </p>
        <div className="d-flex flex-wrap align-items-start" style={{ gap: 16 }}>
          <div style={{ width: 240 }}>
            <NewSelectMultiSelect
              options={FIBRE_OPTIONS}
              value={multiSelect}
              onChange={setMultiSelect}
              placeholder="Select fibres"
              isMulti
              showSearch
              selectAllLabel="Select All"
              clearAllLabel="Clear All"
            />
            <div className="small mt-1 text-muted">multi + search + select-all + clear-all</div>
          </div>
        </div>
      </section>

      {/* ── Checkbox — <NewCheckbox /> ─────────────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Checkbox — NewCheckbox</h4>
        <p className="text-muted">
          Sourced from <code>Components/PIM/common/NewCheckbox</code>. 20px square, radius 4px, explicit border. Optional label renders to the right. Pass{' '}
          <code>invalid</code> for error state and <code>indeterminate</code> for the mixed state.
        </p>

        <div className="d-flex flex-wrap align-items-start" style={{ gap: 32 }}>
          <div>
            <NewCheckbox />
            <div className="small mt-1 text-muted">idle</div>
          </div>
          <div>
            <NewCheckbox defaultChecked />
            <div className="small mt-1 text-muted">checked</div>
          </div>
          <div>
            <NewCheckbox indeterminate />
            <div className="small mt-1 text-muted">indeterminate</div>
          </div>
          <div>
            <NewCheckbox invalid />
            <div className="small mt-1 text-muted">invalid</div>
          </div>
          <div>
            <NewCheckbox disabled />
            <div className="small mt-1 text-muted">disabled</div>
          </div>
          <div>
            <NewCheckbox disabled defaultChecked />
            <div className="small mt-1 text-muted">disabled + checked</div>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-start mt-4" style={{ gap: 32 }}>
          <div>
            <NewCheckbox id="md-cb-1" label="Cotton" checked={checkOne} onChange={(e) => setCheckOne(e.target.checked)} />
            <div className="small mt-1 text-muted">with label</div>
          </div>
          <div>
            <NewCheckbox id="md-cb-2" label="Polyester" checked={checkTwo} onChange={(e) => setCheckTwo(e.target.checked)} />
            <div className="small mt-1 text-muted">with label + checked</div>
          </div>
          <div>
            <NewCheckbox id="md-cb-3" label="Required" invalid />
            <div className="small mt-1 text-muted">invalid + label</div>
          </div>
          <div>
            <NewCheckbox id="md-cb-4" label="Disabled" disabled />
            <div className="small mt-1 text-muted">disabled + label</div>
          </div>
          <div>
            <NewCheckbox id="md-cb-hl" label="Cotton" defaultChecked isHighlight />
            <div className="small mt-1 text-muted">highlight — `isHighlight`</div>
          </div>
        </div>

        <div className="small text-uppercase text-bold mt-4 mb-2">
          16×16 variant — <code>size="sm"</code>
        </div>
        <p className="small text-muted mb-2">
          Compact 16×16 box for grid cells, card headers, and other dense rows. Add <code>size="sm"</code> or the raw class <code>.bombiix-checkbox--sm</code>.
        </p>
        <div className="d-flex flex-wrap align-items-start" style={{ gap: 32 }}>
          <div>
            <NewCheckbox size="sm" />
            <div className="small mt-1 text-muted">idle</div>
          </div>
          <div>
            <NewCheckbox size="sm" defaultChecked />
            <div className="small mt-1 text-muted">checked</div>
          </div>
          <div>
            <NewCheckbox size="sm" indeterminate />
            <div className="small mt-1 text-muted">indeterminate</div>
          </div>
          <div>
            <NewCheckbox size="sm" invalid />
            <div className="small mt-1 text-muted">invalid</div>
          </div>
          <div>
            <NewCheckbox size="sm" disabled />
            <div className="small mt-1 text-muted">disabled</div>
          </div>
          <div>
            <NewCheckbox size="sm" id="md-cb-sm-label" label="Cotton" defaultChecked />
            <div className="small mt-1 text-muted">with label</div>
          </div>
        </div>
      </section>

      {/* ── Radio — <NewRadio /> ───────────────────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Radio — NewRadio</h4>
        <p className="text-muted">
          Sourced from <code>Components/PIM/common/NewRadio</code>. 20px circle, explicit border. Optional label to the right. Group by shared <code>name</code>{' '}
          prop.
        </p>

        <div className="d-flex flex-wrap align-items-start" style={{ gap: 32 }}>
          <div>
            <NewRadio name="md-radio-basic" />
            <div className="small mt-1 text-muted">idle</div>
          </div>
          <div>
            <NewRadio name="md-radio-basic" defaultChecked />
            <div className="small mt-1 text-muted">checked</div>
          </div>
          <div>
            <NewRadio invalid />
            <div className="small mt-1 text-muted">invalid</div>
          </div>
          <div>
            <NewRadio disabled />
            <div className="small mt-1 text-muted">disabled</div>
          </div>
          <div>
            <NewRadio disabled defaultChecked />
            <div className="small mt-1 text-muted">disabled + checked</div>
          </div>
        </div>

        <div className="d-flex flex-wrap align-items-start mt-4" style={{ gap: 32 }}>
          {FIBRE_OPTIONS.slice(0, 3).map((opt) => (
            <div key={opt.value}>
              <NewRadio
                id={`md-radio-${opt.value}`}
                name="md-radio-group"
                label={opt.label}
                value={opt.value}
                checked={radioValue === opt.value}
                onChange={(e) => setRadioValue(e.target.value)}
              />
            </div>
          ))}
          <div>
            <NewRadio id="md-radio-invalid" name="md-radio-group-2" label="Required" invalid />
            <div className="small mt-1 text-muted">invalid + label</div>
          </div>
          <div>
            <NewRadio id="md-radio-disabled" name="md-radio-group-3" label="Disabled" disabled />
            <div className="small mt-1 text-muted">disabled + label</div>
          </div>
          <div>
            <NewRadio id="md-radio-hl" name="md-radio-group-4" label="Cotton" defaultChecked isHighlight />
            <div className="small mt-1 text-muted">highlight — `isHighlight`</div>
          </div>
        </div>
      </section>

      {/* ── Date picker — <NewDatePicker /> ────────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Date picker — NewDatePicker</h4>
        <p className="text-muted">
          Sourced from <code>Components/PIM/common/NewDatePicker</code>. Trigger button (matches text-input chrome) opens a calendar popover with custom month
          header + <code>Set Date</code> confirmation. Supports <code>value</code>, <code>invalid</code>, <code>disabled</code>, and <code>minDate</code>/
          <code>maxDate</code>/<code>filterDate</code> constraints.
        </p>

        <div className="d-flex flex-wrap align-items-start" style={{ gap: 24 }}>
          <div style={{ width: 240 }}>
            <NewDatePicker value={dateIdle} onChange={setDateIdle} />
            <div className="small mt-1 text-muted">idle</div>
          </div>

          <div style={{ width: 240 }}>
            <NewDatePicker value={dateFilled} onChange={setDateFilled} />
            <div className="small mt-1 text-muted">with value</div>
          </div>

          <div style={{ width: 240 }}>
            <NewDatePicker value={dateInvalid} onChange={setDateInvalid} invalid />
            <div className="small mt-1 text-muted">invalid</div>
          </div>

          <div style={{ width: 240 }}>
            <NewDatePicker value={null} onChange={() => {}} disabled />
            <div className="small mt-1 text-muted">disabled</div>
          </div>

          <div style={{ width: 240 }}>
            <NewDatePicker value={dateFilled} onChange={setDateFilled} isHighlight />
            <div className="small mt-1 text-muted">highlight — `isHighlight`</div>
          </div>
        </div>

        <div className="small text-uppercase text-bold mt-4 mb-2">
          Range variant — <code>selectsRange</code>
        </div>
        <p className="small text-muted mb-2">
          Pass <code>selectsRange</code> with <code>startDate</code>/<code>endDate</code>/<code>onRangeChange</code>. Trigger renders{' '}
          <code>DD.Month.YY to DD.Month.YY</code>; days between the anchors get the light-blue range highlight.
        </p>
        <div className="d-flex flex-wrap align-items-start" style={{ gap: 24 }}>
          <div style={{ width: 260 }}>
            <NewDatePicker
              selectsRange
              startDate={rangeStart}
              endDate={rangeEnd}
              onRangeChange={(start, end) => {
                setRangeStart(start);
                setRangeEnd(end);
              }}
            />
            <div className="small mt-1 text-muted">idle</div>
          </div>
          <div style={{ width: 260 }}>
            <NewDatePicker selectsRange startDate={rangeStart} endDate={rangeEnd} onRangeChange={() => {}} invalid />
            <div className="small mt-1 text-muted">invalid</div>
          </div>
          <div style={{ width: 260 }}>
            <NewDatePicker selectsRange startDate={null} endDate={null} onRangeChange={() => {}} disabled />
            <div className="small mt-1 text-muted">disabled</div>
          </div>
        </div>
      </section>

      {/* ── Modal — <BombiixModal /> ───────────────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Modal — BombiixModal</h4>
        <p className="text-muted">
          Sourced from <code>Components/BombiixModal</code>. Wraps <code>react-bootstrap</code> <code>&lt;Modal&gt;</code> with a full-width action bar: blue{' '}
          <strong>Save</strong> on the left, centered title, red <strong>✕</strong> close on the right. Pass <code>onSave</code> / <code>onClose</code>{' '}
          handlers, <code>size</code> (<code>sm</code> / <code>md</code> / <code>lg</code>), and any body via children. Use <code>hideSave</code> /{' '}
          <code>hideClose</code> for read-only or blocking flows; pass a <code>footer</code> node for legacy layouts that still need extra buttons below the
          body. Styling lives in <code>_modal.scss</code>.
        </p>

        <div className="d-flex flex-wrap align-items-start" style={{ gap: 16 }}>
          <button type="button" className="bombiix-btn btn-blue" onClick={() => setModalOpenBasic(true)}>
            Open modal — Save + Close
          </button>
          <button type="button" className="bombiix-btn btn-blue" onClick={() => setModalOpenReadOnly(true)}>
            Open modal — Close only (hideSave)
          </button>
          {/* <button type="button" className="bombiix-btn btn-blue" onClick={() => setModalOpenBlocking(true)}>
            Open modal — Save only (hideClose)
          </button> */}
          <button type="button" className="bombiix-btn btn-blue" onClick={() => setModalOpenFooter(true)}>
            Open modal — with footer slot
          </button>
        </div>

        <BombiixModal
          show={modalOpenBasic}
          title="Add Product Group"
          onClose={() => setModalOpenBasic(false)}
          onSave={() => setModalOpenBasic(false)}
          saveDisabled={modalInputValue.trim().length === 0}
          size="md"
        >
          <NewInput
            label="Product Group"
            placeholder="Enter product group name"
            value={modalInputValue}
            onChange={(event) => setModalInputValue(event.target.value)}
          />
        </BombiixModal>

        <BombiixModal show={modalOpenReadOnly} title="Product details" onClose={() => setModalOpenReadOnly(false)} hideSave size="md">
          <p className="text-muted mb-0">
            Read-only content. The header shows the title and a red close button; no Save action because <code>hideSave</code> is passed.
          </p>
        </BombiixModal>

        <BombiixModal
          show={modalOpenBlocking}
          title="Confirm action"
          onClose={() => setModalOpenBlocking(false)}
          onSave={() => setModalOpenBlocking(false)}
          saveLabel="Confirm"
          hideClose
          size="sm"
          backdrop="static"
        >
          <p className="mb-0">
            Blocking flow — no close button, no backdrop dismiss. User must resolve via <strong>Confirm</strong>.
          </p>
        </BombiixModal>

        <BombiixModal
          show={modalOpenFooter}
          title="Modal with footer"
          onClose={() => setModalOpenFooter(false)}
          onSave={() => setModalOpenFooter(false)}
          size="lg"
          footer={
            <>
              <button type="button" className="bombiix-btn btn-light" onClick={() => setModalOpenFooter(false)}>
                Cancel
              </button>
              <button type="button" className="bombiix-btn btn-blue" onClick={() => setModalOpenFooter(false)}>
                Apply
              </button>
            </>
          }
        >
          <p className="text-muted mb-0">
            Body content plus an optional footer slot. Use the footer only when a modal genuinely needs extra actions beyond the header Save + Close (rare —
            most modals should stick to the pure top-bar layout).
          </p>
        </BombiixModal>
      </section>

      {/* ── Confirm popup — <PimConfirmPopup /> ────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Confirm popup — PimConfirmPopup</h4>
        <p className="text-muted">
          The reusable confirmation built on <code>BombiixModal</code>. <code>confirmPlacement="body"</code> centres the confirm button under the message (Figma
          node 13564:537243); the default <code>"bar"</code> keeps it in the top action bar. Use this rather than hand-assembling a modal body — the component
          owns the spacing so every confirmation matches.
        </p>
        <div className="d-flex flex-wrap align-items-start" style={{ gap: 16 }}>
          <button type="button" className="bombiix-btn btn-red" onClick={() => setModalOpenDelete(true)}>
            Open delete confirmation (body)
          </button>
          <button type="button" className="bombiix-btn btn-blue" onClick={() => setConfirmOpenBar(true)}>
            Open confirmation (bar)
          </button>
        </div>

        <PimConfirmPopup
          open={modalOpenDelete}
          title="Delete BOM"
          message="Are you sure you want to delete this BOM from the BOM library"
          confirmLabel="Delete"
          confirmVariant="red"
          confirmPlacement="body"
          onConfirm={() => setModalOpenDelete(false)}
          onClose={() => setModalOpenDelete(false)}
        />

        <PimConfirmPopup
          open={confirmOpenBar}
          title="Publish template"
          message="This will publish the template to every connected channel."
          confirmLabel="Publish"
          onConfirm={() => setConfirmOpenBar(false)}
          onClose={() => setConfirmOpenBar(false)}
        />
      </section>

      {/* ── Side Panel — <BombiixSidePanel /> ──────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Side Panel — BombiixSidePanel</h4>
        <p className="text-muted">
          Sourced from <code>Components/BombiixSidePanel</code>. Right-anchored drawer with a 42px <code>#F3F3F3</code> action bar (centered 24px Aktiv Grotesk
          title, red 28×28 Close on the right, optional blue Save on the left). Body content is dynamic — use for Share Folder, Comments, PIM Channel Creations,
          Download Options, etc.
        </p>
        <div className="d-flex" style={{ gap: 12 }}>
          <button type="button" className="bombiix-btn btn-blue" onClick={() => setSidePanelBasicOpen(true)}>
            Open — Comments (close only)
          </button>
          <button type="button" className="bombiix-btn btn-blue" onClick={() => setSidePanelSaveOpen(true)}>
            Open — with Save button
          </button>
          <button type="button" className="bombiix-btn btn-blue" onClick={() => setSidePanelHeaderRightOpen(true)}>
            Open — with header actions
          </button>
        </div>

        <BombiixSidePanel isOpen={sidePanelBasicOpen} title="Comments" onClose={() => setSidePanelBasicOpen(false)} anchorSelector={null}>
          <NewTextArea
            label="Comment"
            placeholder="Enter"
            value={sidePanelCommentValue}
            onChange={(event) => setSidePanelCommentValue(event.target.value)}
            rows={4}
          />
        </BombiixSidePanel>

        <BombiixSidePanel
          isOpen={sidePanelSaveOpen}
          title="PIM Channel Creations"
          onClose={() => setSidePanelSaveOpen(false)}
          onSave={() => setSidePanelSaveOpen(false)}
          anchorSelector={null}
        >
          <NewInput label="Channel name" placeholder="Enter" value="" onChange={() => {}} />
          <NewSelectMultiSelect label="Fibres" options={FIBRE_OPTIONS} value={multiSelect} onChange={setMultiSelect} isMulti />
        </BombiixSidePanel>

        <BombiixSidePanel
          isOpen={sidePanelHeaderRightOpen}
          title="Share Folder"
          onClose={() => setSidePanelHeaderRightOpen(false)}
          anchorSelector={null}
          headerRight={
            <button type="button" className="bombiix-icon-container" aria-label="Filter">
              <FilterIcon />
            </button>
          }
        >
          <NewInput label="Recipient" placeholder="Enter email" value="" onChange={() => {}} />
        </BombiixSidePanel>
      </section>

      {/* ── Chips — <BombiixChip /> ────────────────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Chips — BombiixChip</h4>
        <p className="text-muted">
          18px pill with an 11px radius (Figma node 12662:190792). Styling lives in <code>_chip.scss</code> as{' '}
          <code>.bombiix-chip</code>; use the component rather than the class so truncation and the remove control stay consistent. A string child is clamped to
          one line with a hover tooltip (via <code>TruncatedText</code>) — the pill is <code>inline-flex</code>, so its own <code>text-overflow</code> never
          reaches the text. Pair with <code>.bombiix-chip-toggle</code> for the expand / collapse control: transparent while collapsed, taking the chip fill once
          expanded, so the open state reads as part of the chip group.
        </p>
        <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 4 }}>
          {['purple', 'blue', 'green', 'red', 'amber', 'khaki', 'grey'].map((variant) => (
            <BombiixChip key={variant} variant={variant}>
              {variant}
            </BombiixChip>
          ))}
        </div>
        <div className="d-flex flex-wrap align-items-center mb-3" style={{ gap: 8 }}>
          <BombiixChip removeLabel="Remove Bottomwear" onRemove={() => {}}>
            Bottomwear
          </BombiixChip>
          <BombiixChip variant="green" removeLabel="Remove Packaging" onRemove={() => {}}>
            Packaging
          </BombiixChip>
          <span className="text-muted">← removable: pass onRemove + removeLabel together</span>
        </div>
        <div className="d-flex align-items-center mb-3" style={{ gap: 8, maxWidth: 260 }}>
          <BombiixChip>Personal Protective Equipment (PPE) — long enough to clamp</BombiixChip>
        </div>
        <div className="d-flex align-items-center" style={{ gap: 8 }}>
          <BombiixChip>Personal Protective Equipment (PPE)</BombiixChip>
          <button type="button" className="bombiix-chip-toggle" aria-label="Expand">
            <ChevronIcon size={24} />
          </button>
          <button type="button" className="bombiix-chip-toggle bombiix-chip-toggle--open bombiix-chip-toggle--purple" aria-label="Collapse">
            <ChevronIcon size={24} />
          </button>
        </div>
      </section>

      {/* ── Chip select — <BombiixChipSelect /> ─────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Chip select — BombiixChipSelect</h4>
        <p className="text-muted">
          Multi-select whose picks render as removable chips <strong>below</strong> the control instead of inside it. The control shows a read-only summary of
          the selection, so a long list never scrolls the pills out of view. Selection state stays with the caller — <code>onChange</code> fires with the full
          next selection for both a menu pick and a chip removal.
        </p>
        <div style={{ maxWidth: 420 }}>
          <BombiixChipSelect
            label="Product Type"
            required
            options={FIBRE_OPTIONS}
            value={chipSelectValue}
            onChange={setChipSelectValue}
            placeholder="Select"
            allSelectedLabel="All selected"
            selectAllLabel="Select All"
            removeLabel={(option) => `Remove ${option.label}`}
          />
        </div>
      </section>

      {/* ── Sort button — <BombiixSortButton /> ─────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Sort button — BombiixSortButton</h4>
        <p className="text-muted">
          Toolbar sort control: an icon button opening a popover that holds a single-select of sort options. The glyph flips to{' '}
          <code>SortDownIcon</code> while a Descending option is applied, and the trigger carries <code>active</code> so the applied state reads without opening
          the popover. Clearing the select returns the list to its default order.
        </p>
        <div className="d-flex flex-wrap align-items-start" style={{ gap: 24 }}>
          <div>
            <BombiixSortButton options={SORT_OPTIONS} value={sortValue} onChange={setSortValue} sortByLabel="Sort by" ariaLabel="Sort" />
            <div className="small mt-1 text-muted">applied: {sortValue ? sortValue.label : 'none (default order)'}</div>
          </div>
        </div>
      </section>

      {/* ── Info popover — <BombiixInfoPopover /> ──────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Info popover — BombiixInfoPopover</h4>
        <p className="text-muted">
          Label / value audit popover on hover or focus (Figma node 13564:539061). Takes a <code>rows</code> array, so it is not limited to created / modified
          stamps. Styling lives in <code>_popover.scss</code> as <code>.bombiix-info-popover</code>.
        </p>
        <BombiixInfoPopover
          placement="right"
          ariaLabel="Record information"
          rows={[
            { label: 'Created by:', value: 'Dean Smith, 12-August-2026, 09:23 am' },
            { label: 'Modified by:', value: 'Dean Smith, 12-August-2026, 09:23 am' },
          ]}
        />
      </section>

      {/* ── Rearrange panel — <BombiixRearrangePanel /> ────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Rearrange panel — BombiixRearrangePanel</h4>
        <p className="text-muted">
          The whole Rearrange Sequence view in one component (Figma node 13564:537594) — side-panel chrome, body padding and the reorder list together. It takes
          no <code>className</code> on purpose: spacing lives in <code>_rearrange-panel.scss</code> so every Rearrange view stays identical.
        </p>
        <button type="button" className="bombiix-btn btn-blue" onClick={() => setRearrangeOpen(true)}>
          Open Rearrange Sequence
        </button>
        <BombiixRearrangePanel
          isOpen={rearrangeOpen}
          title="Rearrange Sequence"
          items={reorderDemo}
          onReorder={setReorderDemo}
          onSave={() => setRearrangeOpen(false)}
          onClose={() => setRearrangeOpen(false)}
          saveLabel="Save"
          moveHint="Move"
          anchorSelector={null}
        />
      </section>

      {/* ── Cards — <BombiixCard /> ────────────────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Cards — BombiixCard</h4>
        <p className="text-muted">
          Compound container from <code>Components/Card/BombiixCard</code>. Design tokens (4px radius, 1px <code>#D8D8D8</code> border, <code>#F3F3F3</code> bg,
          on-hover dropshadow) live in <code>_card.scss</code>. Consumers wire icons, image, title, meta and tag chips. Hover a card to see the dropshadow
          transition. Pass <code>enlargeOnHover</code> to <code>BombiixCard.Thumb</code> to pop a 200×200 preview out below-left of the image on hover — see the
          Product Card below.
        </p>

        <div className="d-flex flex-wrap align-items-start" style={{ gap: 24 }}>
          {/* Product Card variant — 345×245 (Figma node 4734:151749) */}
          <div style={{ width: 345 }}>
            <BombiixCard>
              <BombiixCard.Header>
                <BombiixCard.Actions>
                  <li>
                    <NewCheckbox size="sm" readOnly aria-label="Select" />
                  </li>
                  <li>
                    <button type="button" className="bombiix-icon-container bombiix-icon-container--blue" aria-label="Progress">
                      <ProgressIndicatorIcon size={24} />
                    </button>
                  </li>
                  <li>
                    <button type="button" className="bombiix-icon-container" aria-label="Edit">
                      <EditIcon size={24} />
                    </button>
                  </li>
                  <li>
                    <button type="button" className="bombiix-icon-container bombiix-icon-container--dark" aria-label="Archive">
                      <ArchiveIcon size={24} />
                    </button>
                  </li>
                  <li>
                    <button type="button" className="bombiix-icon-container" aria-label="Duplicate">
                      <DuplicateIcon size={24} />
                    </button>
                  </li>
                </BombiixCard.Actions>
                <BombiixCard.Thumb alt="Product SMVR-001" enlargeOnHover style={{ background: '#e8dfd4' }} />
              </BombiixCard.Header>
              <BombiixCard.Body>
                <BombiixCard.Title>SMVR-001</BombiixCard.Title>
                <BombiixCard.Meta>
                  <li>Commuter Jacket</li>
                  <li>Samvaer</li>
                  <li>Winter 2025</li>
                  <li>Men, Clothing, Jackets and Coats, Jacket</li>
                </BombiixCard.Meta>
              </BombiixCard.Body>
              <BombiixCard.Footer>
                <BombiixCard.Tag variant="green">Product Status</BombiixCard.Tag>
                <BombiixCard.Tag>Colour Status</BombiixCard.Tag>
                <BombiixCard.Tag variant="amber">License Status</BombiixCard.Tag>
              </BombiixCard.Footer>
            </BombiixCard>
            <div className="small mt-1 text-muted">Product Card — 345×245</div>
          </div>

          {/* BOM Card variant — 345×356 */}
          <div style={{ width: 345 }}>
            <BombiixCard style={{ minHeight: 356 }}>
              <BombiixCard.Header>
                <BombiixCard.Actions>
                  <li>
                    <NewCheckbox size="sm" readOnly aria-label="Select" />
                  </li>
                  <li>
                    <button type="button" className="bombiix-icon-container bombiix-icon-container--blue" aria-label="Edit">
                      <EditIcon size={24} />
                    </button>
                  </li>
                  <li>
                    <button type="button" className="bombiix-icon-container bombiix-icon-container--red" aria-label="Delete">
                      <DeleteIcon size={24} />
                    </button>
                  </li>
                </BombiixCard.Actions>
                <BombiixCard.Thumb alt="BOM MM-CH-Tweed" style={{ background: '#5a3a2a' }} />
              </BombiixCard.Header>
              <BombiixCard.Body>
                <BombiixCard.Title>MM-CH-Tweed</BombiixCard.Title>
                <BombiixCard.Meta>
                  <li>
                    <strong>Type:</strong> Natural
                  </li>
                  <li>
                    <strong>Description:</strong> Tweed
                  </li>
                  <li>
                    <strong>Supplier:</strong> Martin Mills
                  </li>
                  <li>
                    <strong>Placement:</strong> Shell
                  </li>
                  <li>
                    <strong>Composition:</strong> 100% Wool
                  </li>
                  <li>
                    <strong>Weight:</strong> 310 GSM
                  </li>
                  <li>
                    <strong>Size/Volume:</strong> 55 (Cutting Width) Inch(es)
                  </li>
                  <li>
                    <strong>Quantity:</strong> 2,14 (avg. Per Unit) Metres
                  </li>
                  <li>
                    <strong>Additional Info:</strong> Number of Needles 132, Lorem Ipsum, Lorem
                  </li>
                </BombiixCard.Meta>
              </BombiixCard.Body>
            </BombiixCard>
            <div className="small mt-1 text-muted">BOM Card — 345×356</div>
          </div>
        </div>
      </section>

      {/* ── Editable grid — <BombiixAGGrid /> ───────────────────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Editable grid — BombiixAGGrid</h4>
        <p className="text-muted">
          Sourced from <code>Components/BombiixAGGrid</code>. Text / currency cells use <code>NewInput</code>, checkbox uses <code>NewCheckbox</code>, radio
          uses <code>NewRadio</code>, and the dropdown uses <code>NewSelectMultiSelect</code>. Delete icon is wired to the trailing action column; footer row
          shows <code>__cagFooter</code> totals. Long column titles are cut off with an ellipsis and show the full title in a tooltip on hover — only when
          they are actually truncated (see <strong>Planned Production Start Date (Supplier Confirmed)</strong>; widen the column and the tooltip goes away).
          The required <code>*</code> sits outside the truncated text so it is never cut off. This is built into <code>SortableHeader</code> via{' '}
          <code>TruncatedText</code>, so every grid gets it with no per-column setup (a grid with <code>autoSizeColumns</code> widens columns to fit
          their titles instead, so it never truncates them). Plain-text cells behave the same way through{' '}
          <code>tooltipShowMode=&quot;whenTruncated&quot;</code>; cells with a custom renderer are left to manage their own tooltip.
        </p>
        <BombiixAGGrid
          columnDefs={gridColumnDefs}
          rowData={gridRows}
          pinnedBottomRowData={gridFooterRows}
          onRowChange={handleGridRowChange}
          onDeleteRow={(rowId) => setGridRows((prev) => prev.filter((row) => row.id !== rowId))}
          radioGroupName="master-design-grid-default"
          selectedRadioRowId={gridRadio}
          getRowId={(row) => row.id}
          detailRowRenderer={GridDetailRenderer}
          isRowMaster={() => true}
          enableFillHandle
          fillHandleDirection="y"
        />
      </section>

      <section className="mb-5">
        <h4 className="mb-3">Editable grid — right-click Cut / Copy / Paste</h4>
        <p className="text-muted">
          Same grid as above with <code>enableClipboardContextMenu</code> turned on. Right-click any cell for a menu with <strong>Cut</strong>,{' '}
          <strong>Copy</strong> and <strong>Paste</strong> (<kbd>Ctrl+X</kbd> / <kbd>Ctrl+C</kbd> / <kbd>Ctrl+V</kbd>). The prop is <code>false</code> by
          default, so the grid above shows no menu at all. Cut and Paste only work on editable cells — AG Grid greys them out on read-only ones such as{' '}
          <strong>Created</strong> — and a cell counts as editable here because <code>enableFillHandle</code> is on. Menu Paste reads the system clipboard,
          which browsers only allow on HTTPS (or <code>localhost</code>), so <strong>Paste is hidden on a plain-HTTP host</strong> — there AG Grid's
          fallback would paste an empty string and wipe the cell. Keyboard paste is unaffected by this prop. The same prop exists on <code>AG-GridTable/AGGridTable</code> (Sample To Do uses it). Both grids
          share one row state on this page, so a cut here also clears the cell in the grid above.
        </p>
        <BombiixAGGrid
          columnDefs={gridColumnDefs}
          rowData={gridRows}
          onRowChange={handleGridRowChange}
          radioGroupName="master-design-grid-clipboard-default"
          selectedRadioRowId={gridRadio}
          getRowId={(row) => row.id}
          enableFillHandle
          fillHandleDirection="y"
          enableClipboardContextMenu
        />
      </section>

      {/* ── Tree-data grid — Products with colour children ───────────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Editable tree grid — product with colours</h4>
        <p className="text-muted">
          <code>treeData</code> + <code>getDataPath</code> mode. Each product row expands to its colour rows using the same column set. All rows expanded by
          default via <code>groupDefaultExpanded={'{-1}'}</code>. This grid also doubles as the AG-Grid adornment demo — <strong>Product Description</strong>{' '}
          uses <code>NewInput</code> with a checkbox <code>startAdornment</code> and a <code>10/200</code> counter <code>endAdornment</code>;{' '}
          <strong>Drp1</strong> / <strong>Drp2</strong> use a <code>+</code> add-button <code>endAdornment</code>; <strong>Licensor</strong> nests the shared{' '}
          <code>.design-switch</code> markup inside <code>NewInput</code>; <strong>Price</strong> uses a <code>£</code> <code>startAdornment</code>;{' '}
          <strong>Discount</strong> uses a <code>%</code> <code>endAdornment</code>; and <strong>Action</strong> is a destructive{' '}
          <code>InputAdornmentButton</code>. Copy any renderer to plug the master-design adornments into your own grid column.
        </p>
        <BombiixAGGrid
          columnDefs={productColumnDefs}
          rowData={productRows}
          onRowChange={handleProductRowChange}
          getRowId={(row) => row.id}
          treeData
          getDataPath={(row) => row.path}
          groupDefaultExpanded={-1}
          autoGroupColumnDef={{ pinned: false }}
        />
      </section>

      {/* ── Approval bar — bottom-of-page approve / reject action row ────── */}
      <section className="mb-5">
        <h4 className="mb-3">Approval bar</h4>
        <p className="text-muted">
          Bottom-of-page action row (Figma node 9309:561030). Fully controlled — the parent owns every value. The segmented statuses come from an{' '}
          <code>options</code> prop rather than being hardcoded, so each module passes only the statuses its own API accepts;{' '}
          <strong>Approved with comments</strong> is in the design but is not yet understood by any endpoint. The selected fill follows the option’s{' '}
          <code>variant</code> — green by default, <code>red</code> for a destructive status like Rejected (Figma node 7968:139640) — so the colour is declared
          per option rather than guessed from the label. Each field is toggled independently via <code>showStatusType</code> / <code>showDatePicker</code> /{' '}
          <code>showSupplier</code> / <code>showComment</code>, and the row stays correctly spaced with any subset visible. Positioning is the caller’s choice
          via <code>className</code> — the bar itself sets no <code>position</code>, so it works sticky on a page or dropped into a modal footer.
        </p>

        <div className="mb-2 small text-muted">
          Three options, every field — the full Figma layout. Pinned to the bottom of the viewport via <code>bombiix-approval-bar--fixed</code>, so it stays put
          while the rest of the page scrolls.
        </div>
        <BombiixApprovalBar
          className="bombiix-approval-bar--fixed"
          options={APPROVAL_OPTIONS_THREE}
          value={approvalFull}
          onChange={setApprovalFull}
          showStatusType
          statusTypeOptions={STATUS_TYPE_OPTIONS}
          statusType={approvalFullType}
          onStatusTypeChange={setApprovalFullType}
          showDatePicker
          date={approvalFullDate}
          onDateChange={setApprovalFullDate}
          showSupplier
          supplierOptions={SUPPLIER_OPTIONS}
          supplier={approvalFullSupplier}
          onSupplierChange={setApprovalFullSupplier}
          showComment
          showCommentCounter
          comment={approvalFullComment}
          onCommentChange={setApprovalFullComment}
          onSave={() => {}}
        />
        {/* The fixed bar is out of flow — reserve the row it would have filled
            so the demos below don't jump up under it. */}
        <div className="bombiix-approval-bar-spacer mb-4" />

        <div className="mb-2 small text-muted">
          Two options, no supplier or status type — matches today’s approve/reject endpoints. Select <strong>Rejected</strong> to see the red fill.
        </div>
        <BombiixApprovalBar
          className="mb-4"
          options={APPROVAL_OPTIONS_TWO}
          value={approvalTwo}
          onChange={setApprovalTwo}
          showDatePicker
          date={approvalTwoDate}
          onDateChange={setApprovalTwoDate}
          showComment
          comment={approvalTwoComment}
          onCommentChange={setApprovalTwoComment}
          onSave={() => {}}
        />

        <div className="mb-2 small text-muted">
          Comment required — <strong>Rejected</strong> needs a comment, so the field shows the invalid state (red border + alert icon) while empty. The counter
          stays on the right and clears the alert icon. Type a comment to clear it; Save is blocked until then.
        </div>
        <BombiixApprovalBar
          className="mb-4"
          options={APPROVAL_OPTIONS_TWO}
          value={approvalValidate}
          onChange={setApprovalValidate}
          showComment
          showCommentCounter
          comment={approvalValidateComment}
          onCommentChange={setApprovalValidateComment}
          commentInvalid={approvalValidateCommentInvalid}
          saveDisabled={approvalValidateCommentInvalid}
          onSave={() => {}}
        />

        <div className="mb-2 small text-muted">Segments only — nothing selected, save disabled</div>
        <BombiixApprovalBar className="mb-4" options={APPROVAL_OPTIONS_TWO} value={null} onChange={() => {}} saveDisabled onSave={() => {}} />

        <div className="mb-2 small text-muted">
          Disabled — every control read-only. The selected segment stays legible so an archived record still shows its status.
        </div>
        <BombiixApprovalBar
          options={APPROVAL_OPTIONS_THREE}
          value={approvalMinimal}
          onChange={setApprovalMinimal}
          showDatePicker
          date={new Date()}
          showSupplier
          supplierOptions={SUPPLIER_OPTIONS}
          supplier={SUPPLIER_OPTIONS[0]}
          showComment
          comment="Approved on inspection"
          disabled
          onSave={() => {}}
        />
      </section>

      {/* ── Tab switch — <CommonTabSwitch /> (segmented pill) ──────────── */}
      <section className="mb-5">
        <h4 className="mb-3">Tab switch — CommonTabSwitch</h4>
        <p className="text-muted">
          Segmented pill tab switch (Figma node <code>12358:160579</code>). A white rounded container with an inset rim holds pill tabs; a green pill{' '}
          (<code>$color-green</code>) slides behind the active tab. Sourced from <code>Components/CommonTabSwitch/CommonTabSwitch</code>, styled by its own{' '}
          <code>CommonTabSwitch.scss</code>. Props mirror the <code>ProductCostingTabMenu</code> switch usage — <code>masterActiveTab</code>,{' '}
          <code>MenuList</code>, <code>isAPILoading</code>, <code>activeTheTab</code>.
        </p>

        <div className="d-flex flex-column" style={{ gap: 24 }}>
          <div>
            <CommonTabSwitch masterActiveTab={activeTabSwitch} MenuList={TAB_SWITCH_OPTIONS} activeTheTab={setActiveTabSwitch} />
            <div className="small mt-1 text-muted">
              default — active: <code>{activeTabSwitch}</code>
            </div>
          </div>

          <div>
            <CommonTabSwitch
              masterActiveTab={activeTabSwitchDisabled}
              MenuList={[
                { eventKey: 'materials', eventName: 'Materials' },
                { eventKey: 'artwork', eventName: 'Artwork & Applications' },
                { eventKey: 'trims', eventName: 'Trims', isDisabled: true },
                { eventKey: 'labels', eventName: 'Labels' },
              ]}
              activeTheTab={setActiveTabSwitchDisabled}
            />
            <div className="small mt-1 text-muted">with a disabled tab (Trims)</div>
          </div>

          <div>
            <CommonTabSwitch masterActiveTab="materials" MenuList={TAB_SWITCH_OPTIONS} isAPILoading activeTheTab={() => {}} />
            <div className="small mt-1 text-muted">isAPILoading — every tab disabled while an API call is in flight</div>
          </div>

          <div style={{ maxWidth: 520 }}>
            <CommonTabSwitch masterActiveTab={activeTabSwitchOverflow} MenuList={TAB_SWITCH_OVERFLOW_OPTIONS} activeTheTab={setActiveTabSwitchOverflow} />
            <div className="small mt-1 text-muted">
              overflow — tabs that don’t fit collapse into the <code>MoreIcon</code> kebab; click it to pick a hidden tab (Figma node <code>12948:715059</code>).
              Active: <code>{activeTabSwitchOverflow}</code>
            </div>
          </div>
        </div>
      </section>

      {/* ── Rich text — <CommonRichTextField /> (master formatted-text field) ── */}
      <section className="mb-5">
        <h4 className="mb-3">Rich text — CommonRichTextField</h4>
        <p className="text-muted">
          Master field for any value that is formatted text (Figma node <code>13738:101728</code>) — the label sits inside the grey toolbar strip, so the
          control reads as one bordered box. Sourced from <code>Components/CommonRichTextField/CommonRichTextField</code>, built on the vendored CKEditor 5
          build repainted with <code>bombiix-icon</code> glyphs. The value is HTML: readers that print it as plain text need <code>htmlToText</code>, and{' '}
          <code>maxLength</code> counts visible characters, not markup. Sanitizing is internal — never swap in design detail&apos;s <code>sanitizeHtml</code>,
          which drops font colour, strikethrough and quotes. Full notes in <code>docs/common-components.md</code>.
        </p>

        <div className="d-flex flex-column" style={{ gap: 24 }}>
          <div style={{ maxWidth: 520 }}>
            <CommonRichTextField label="Additional Info" value={richTextEmpty} onChange={setRichTextEmpty} placeholder="Add a description" />
            <div className="small mt-1 text-muted">idle — empty, showing the placeholder</div>
          </div>

          <div style={{ maxWidth: 520 }}>
            <CommonRichTextField label="Additional Info" value={richTextFilled} onChange={setRichTextFilled} />
            <div className="small mt-1 text-muted">with value — bold and a font colour, both of which survive a save/reload round trip</div>
          </div>

          <div style={{ maxWidth: 520 }}>
            <CommonRichTextField label="Notes" value={richTextCapped} onChange={setRichTextCapped} maxLength={80} minHeight={72} />
            <div className="small mt-1 text-muted">
              maxLength=80 · minHeight=72 — {richTextLength(richTextCapped)}/80 visible characters. An edit that would exceed the cap is rejected outright, so
              the counter can never run past it.
            </div>
          </div>

          <div style={{ maxWidth: 520 }}>
            <CommonRichTextField label="Additional Info" value={richTextFilled} onChange={() => {}} disabled />
            <div className="small mt-1 text-muted">disabled — read-only, toolbar inert</div>
          </div>
        </div>
      </section>

      <section className="mb-5">
        <h4 className="mb-3">Upload drop box — BombiixDropzone</h4>
        <p className="text-muted">
          Master upload area (Figma node <code>5030:403681</code> — Upload Image / Upload PDF). Sourced from <code>Components/BombiixDropzone</code>; styling
          lives in <code>_image-dropzone.scss</code>. <code>type=&quot;image&quot;</code> shows the image glyph; <code>type=&quot;file&quot;</code> shows the
          document glyph and, with <code>showExtensions</code>, lists the dotted extensions from <code>accept</code>. Pass <code>preview</code> to replace the
          glyph (e.g. a crop) — click-to-open then turns off so the preview stays usable — and <code>footer</code> for content under the text, such as the
          picked file name. Used by <code>ImgDropzone</code> and <code>UploadFileAndDocument</code> through <code>variant=&quot;asset&quot;</code>.
        </p>

        <div className="row">
          <div className="col-md-6 mb-3">
            <BombiixDropzone accept=".jpeg,.jpg,.png" onDrop={() => {}} />
            <div className="small mt-1 text-muted">type=&quot;image&quot; — idle</div>
          </div>
          <div className="col-md-6 mb-3">
            <BombiixDropzone type="file" accept=".xls,.xlsx,.pdf,.dxf,.rul,.wds" onDrop={() => {}} showExtensions />
            <div className="small mt-1 text-muted">type=&quot;file&quot; + showExtensions — idle</div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default MasterDesign;
